import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  BarChart3,
  Users,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  ChevronDown,
  MoreVertical,
  ExternalLink,
  Menu,
  Settings,
  CreditCard,
  Receipt as ReceiptIcon,
  LayoutDashboard,
  Box,
  ListOrdered,
  UserCircle2,
  ChevronLeft,
  ChevronRight,
  Ship,
  Building2,
  Layers,
  Calculator,
  ScanLine,
  Tags,
  Wallet,
  Star,
  MessageSquare,
} from 'lucide-react';
import './Admin.css';
import AdminShipmentsWorkspace from '../components/AdminShipmentsWorkspace';
import AdminShippingAgentsPanel from '../components/AdminShippingAgentsPanel';
import AdminFinancePanel from '../components/AdminFinancePanel';
import AdminReviewsPanel from '../components/AdminReviewsPanel';
import AdminEnquiriesPanel from '../components/AdminEnquiriesPanel';
import AdminReportsPanel from '../components/AdminReportsPanel';
import AdminDashboard from '../components/AdminDashboard';
import AdminNotificationPanel from '../components/AdminNotificationPanel';
import AdminCustomersPanel from '../components/AdminCustomersPanel';
import AdminConsolidateWizard, { isConsolidatedParent } from '../components/AdminConsolidateWizard';
import ThemeToggle from '../components/ThemeToggle';
import Receipt from '../components/documents/Receipt';
import Invoice from '../components/documents/Invoice';
import HBL from '../components/documents/HBL';
import PackageStickers from '../components/documents/PackageStickers';
import AdminPackageScan from './AdminPackageScan';
import CbmCalculator from '../components/CbmCalculator';
import { canPrintStickers, isDoorToDoor } from '../utils/collectionPackages';
import API_BASE from '../services/apiBase';
import { fetchAuthMe, adminDisableMfa } from '../services/adminAuthApi';
import { getAdminPasswordHeaders, ensureAdminApiPassword, clearAdminApiPassword } from '../services/http';
import { useAuth } from '../features/auth/AuthContext';
import { computeCbmFromDimensions } from '../utils/cbm';
import { COMPANY_LOGO_SRC } from '../constants/branding';
import { filterNavSections, canAccessAdminTab, defaultTabForRole } from '../utils/adminAccess';

const ADMIN_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const ADMIN_IMAGE_NAME_RE = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i;

/** Dev-only logging — keeps production console clean. */
const adminDebugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') console.log(...args);
};

/** Some devices send HEIC/WebP with an empty `file.type`; fall back on extension. */
function isLikelyAdminImageFile(file) {
  if (!file) return false;
  if (typeof file.type === 'string' && file.type.startsWith('image/')) return true;
  return ADMIN_IMAGE_NAME_RE.test(file.name || '');
}

const Admin = () => {
  const getTodayYmd = () => new Date().toISOString().slice(0, 10);
  const navigate = useNavigate();
  const { user: authUser, updateUser, logout: authLogout } = useAuth();
  const mfaLoadRef = useRef(false);
  const [mfaSetup, setMfaSetup] = useState({
    qr: '',
    manualKey: '',
    code: '',
    err: '',
    busy: false,
    info: '',
  });
  const computeCbm = computeCbmFromDimensions;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [collectionsFilter, setCollectionsFilter] = useState({
    query: '',
    status: 'all',
    clearing: 'all',
  });
  const [emptyBoxesFilter, setEmptyBoxesFilter] = useState({ query: '', status: 'all' });
  const [boxItemsFilter, setBoxItemsFilter] = useState({ query: '' });
  const [usersFilter, setUsersFilter] = useState({ query: '', role: 'all', active: 'all' });
  const [customersFilter, setCustomersFilter] = useState({ query: '' });
  const [showBoxPreview, setShowBoxPreview] = useState(false);
  const [isSubmittingCollection, setIsSubmittingCollection] = useState(false);
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState(null);
  const [searchingShipper, setSearchingShipper] = useState(false);
  const [searchingConsignee, setSearchingConsignee] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showHBL, setShowHBL] = useState(false);
  const [showStickers, setShowStickers] = useState(false);
  const [stickerParentCollection, setStickerParentCollection] = useState(null);
  const [stickerChildCollections, setStickerChildCollections] = useState([]);
  const [hblChildCollections, setHblChildCollections] = useState([]);
  const [showConsolidateWizard, setShowConsolidateWizard] = useState(false);
  const [consolidateWizardMode, setConsolidateWizardMode] = useState('create');
  const [consolidateParent, setConsolidateParent] = useState(null);
  const [showCollectionPreview, setShowCollectionPreview] = useState(false);
  const [isEditingCollection, setIsEditingCollection] = useState(false);
  const [editingCollectionData, setEditingCollectionData] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedShipper, setSelectedShipper] = useState(null);
  const [selectedConsignee, setSelectedConsignee] = useState(null);
  const [customerTab, setCustomerTab] = useState('shippers');
  const [shippersList, setShippersList] = useState([]);
  const [consigneesList, setConsigneesList] = useState([]);
  const [loadingShippers, setLoadingShippers] = useState(false);
  const [loadingConsignees, setLoadingConsignees] = useState(false);
  const [savingShipper, setSavingShipper] = useState(false);
  const [savingConsignee, setSavingConsignee] = useState(false);
  const [selectedShipperCustomer, setSelectedShipperCustomer] = useState(null);
  const [selectedConsigneeCustomer, setSelectedConsigneeCustomer] = useState(null);
  /** customers tab right panel: view (read-only), edit, or add */
  const [customerPanelMode, setCustomerPanelMode] = useState('add');
  const [shipperForm, setShipperForm] = useState({
    nameWithInitials: '',
    passportNo: '',
    address: '',
    mobilesText: '',
    passportPhoto: null,
    passportPhotoUrl: ''
  });
  const [consigneeForm, setConsigneeForm] = useState({
    nameWithInitials: '',
    idType: 'passport',
    passportNo: '',
    nicNo: '',
    drivingLicenceNo: '',
    address: '',
    mobiles: [''],
    passportPhoto: null,
    passportPhotoUrl: '',
    drivingLicencePhoto: null,
    drivingLicencePhotoUrl: '',
    nicFront: null,
    nicFrontUrl: '',
    nicBack: null,
    nicBackUrl: ''
  });
  const [emptyBoxes, setEmptyBoxes] = useState([]);
  const [loadingEmptyBoxes, setLoadingEmptyBoxes] = useState(false);
  const [savingEmptyBox, setSavingEmptyBox] = useState(false);
  const [deletingEmptyBoxId, setDeletingEmptyBoxId] = useState('');
  const [boxItems, setBoxItems] = useState([]);
  const [loadingBoxItems, setLoadingBoxItems] = useState(false);
  const [savingBoxItem, setSavingBoxItem] = useState(false);
  const [deletingBoxItemId, setDeletingBoxItemId] = useState('');
  const [editingBoxItemId, setEditingBoxItemId] = useState('');
  const [showEmptyBoxModal, setShowEmptyBoxModal] = useState(false);
  const [editingEmptyBoxId, setEditingEmptyBoxId] = useState('');
  const [boxItemForm, setBoxItemForm] = useState({
    itemName: '',
    officialName: ''
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [topUserMenuOpen, setTopUserMenuOpen] = useState(false);
  const [sidebarUserMenuOpen, setSidebarUserMenuOpen] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [appUsersLoading, setAppUsersLoading] = useState(false);
  const [appUsersError, setAppUsersError] = useState('');
  const [newPortalUser, setNewPortalUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'customer',
    staffDepartment: 'full',
  });
  const [creatingPortalUser, setCreatingPortalUser] = useState(false);
  const [securityMfaEnabled, setSecurityMfaEnabled] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityMessage, setSecurityMessage] = useState({ type: '', text: '' });
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [collectionsPage, setCollectionsPage] = useState(1);
  const topSearchRef = useRef(null);
  const [emptyBoxForm, setEmptyBoxForm] = useState({
    boxName: '',
    secondName: '',
    widthInch: '',
    lengthInch: '',
    heightInch: '',
    weightKg: '',
    priceUpbGift: '',
    priceDoorToDoor: '',
    quantity: 0,
    description: '',
    boxImage: null,
    boxImageUrl: '',
    status: 'Active'
  });
  const [boxCollectionForm, setBoxCollectionForm] = useState({
    collectionNo: '',
    collectionDate: getTodayYmd(),
    clearingType: '',
    boxDimensionUnit: 'inch',
    shipper: {
      nameWithInitials: '',
      passportNo: '',
      address: '',
      mobiles: [''],
      passportPhoto: null
    },
    consignee: {
      nameWithInitials: '',
      idType: '', // 'passport' | 'nic' | 'driving_licence'
      passportNo: '',
      nicNo: '',
      drivingLicenceNo: '',
      address: '',
      mobiles: [''],
      passportPhoto: null,
      drivingLicencePhoto: null,
      nicFront: null,
      nicBack: null
    },
    charges: {
      transportCharge: '',
      packingCharge: '',
      otherCharge: '',
      discountType: 'percentage', // 'percentage' | 'amount'
      discountValue: ''
    },
    boxes: [
      {
        boxType: '',
        officialBoxName: '',
        d2d: '',
        description: '',
        dimensionsUnit: 'inch', // 'cm' | 'inch'
        length: '',
        width: '',
        height: '',
        cbm: '',
        totalCbm: '',
        weight: '',
        totalWeight: '',
        price: '',
        totalPrice: '',
        quantity: 1
      }
    ]
  });

  const updateBoxForm = (path, value) => {
    setBoxCollectionForm(prev => {
      const segments = Array.isArray(path) ? path : path.split('.');
      const clone = { ...prev };
      let cur = clone;
      let src = prev;
      for (let i = 0; i < segments.length - 1; i++) {
        const key = segments[i];
        // create shallow copy to preserve File objects on siblings
        cur[key] = Array.isArray(src[key]) ? [...src[key]] : { ...src[key] };
        cur = cur[key];
        src = src[key];
      }
      cur[segments[segments.length - 1]] = value;
      return clone;
    });
  };

  const addMobile = (section) => {
    setBoxCollectionForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        mobiles: [...prev[section].mobiles, '']
      }
    }));
  };

  // Backend integration
  const upsertPerson = async (type, payload) => {
    // payload should be FormData to support images
    adminDebugLog(`=== SAVING ${type.toUpperCase()} ===`);
    adminDebugLog(`${type} API URL:`, `${API_BASE}/api/${type}s/upsert`);
    adminDebugLog(`${type} payload:`, payload);
    
    const res = await fetch(`${API_BASE}/api/${type}s/upsert`, {
      method: 'POST',
      body: payload,
    });
    
    adminDebugLog(`${type} response status:`, res.status);
    adminDebugLog(`${type} response ok:`, res.ok);
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error(`${type} error response:`, errorText);
      throw new Error(`${type} save failed: ${res.status} - ${errorText}`);
    }
    
    const result = await res.json();
    adminDebugLog(`${type} saved successfully:`, result);
    return result;
  };

  const inferConsigneeIdType = (consignee, consigneeId) => {
    if (consignee?.idType) return consignee.idType;
    if (consignee?.nicNo) return 'nic';
    if (consignee?.drivingLicenceNo) return 'driving_licence';
    if (consignee?.passportNo) return 'passport';
    const id = String(consigneeId || '').trim();
    if (/V$/i.test(id)) return 'nic';
    return 'passport';
  };

  const upsertShipperFromCollectionEdit = async (shipper, passportNo) => {
    const trimmedPassport = String(passportNo || '').trim();
    if (!trimmedPassport || !shipper) return null;
    const fd = new FormData();
    fd.append('nameWithInitials', shipper.nameWithInitials || '');
    fd.append('passportNo', trimmedPassport);
    fd.append('address', shipper.address || '');
    normalizeMobiles(shipper.mobiles).forEach((mobile, index) => {
      fd.append(`mobiles[${index}]`, mobile);
    });
    return upsertPerson('shipper', fd);
  };

  const upsertConsigneeFromCollectionEdit = async (consignee, consigneeId) => {
    const trimmedId = String(consigneeId || '').trim();
    if (!trimmedId || !consignee) return null;
    const idType = inferConsigneeIdType(consignee, trimmedId);
    const fd = new FormData();
    fd.append('nameWithInitials', consignee.nameWithInitials || '');
    fd.append('idType', idType);
    fd.append(
      'passportNo',
      consignee.passportNo || (idType === 'passport' ? trimmedId : ''),
    );
    fd.append('nicNo', consignee.nicNo || (idType === 'nic' ? trimmedId : ''));
    fd.append('drivingLicenceNo', consignee.drivingLicenceNo || (idType === 'driving_licence' ? trimmedId : ''));
    fd.append('address', consignee.address || '');
    normalizeMobiles(consignee.mobiles).forEach((mobile, index) => {
      fd.append(`mobiles[${index}]`, mobile);
    });
    return upsertPerson('consignee', fd);
  };

  const fetchCollections = useCallback(async () => {
    setLoadingCollections(true);
    try {
      adminDebugLog('Loading collections from:', `${API_BASE}/api/collections`);
      const res = await fetch(`${API_BASE}/api/collections`);
      adminDebugLog('Response status:', res.status);

      if (res.ok) {
        const data = await res.json();
        adminDebugLog('Collections loaded:', data.length);
        setCollections(data);
      } else {
        const errorText = await res.text();
        console.error('Failed to load collections:', res.status, errorText);
        alert('Failed to load collections: ' + res.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      alert('Failed to load collections: ' + error.message);
    } finally {
      setLoadingCollections(false);
    }
  }, []);

  const normalizeMobiles = (mobiles) => {
    if (Array.isArray(mobiles)) return mobiles.filter(Boolean);
    if (typeof mobiles === 'string' && mobiles.trim()) return [mobiles.trim()];
    return [];
  };

  const fallbackPerson = (idValue = '') => ({
    nameWithInitials: 'N/A',
    address: 'N/A',
    mobiles: ['N/A'],
    passportNo: idValue || 'N/A',
    nicNo: idValue || 'N/A'
  });

  const fetchShipperData = async (passportNo) => {
    if (!passportNo) return null;
    try {
      const res = await fetch(`${API_BASE}/api/shippers/by-passport/${encodeURIComponent(passportNo)}`);
      if (!res.ok) return null;
      const data = await res.json();
      return { ...data, mobiles: normalizeMobiles(data?.mobiles) };
    } catch (error) {
      console.warn('Failed to fetch shipper details:', error);
      return null;
    }
  };

  const fetchConsigneeData = async (consigneeId) => {
    if (!consigneeId) return fallbackPerson('');
    const encodedId = encodeURIComponent(consigneeId);
    try {
      // Try ID-based lookup first because some legacy records store Mongo _id
      if (/^[0-9a-fA-F]{24}$/.test(consigneeId)) {
        const byIdRes = await fetch(`${API_BASE}/api/consignees/by-id/${encodedId}`);
        if (byIdRes.ok) {
          const data = await byIdRes.json();
          return { ...data, mobiles: normalizeMobiles(data?.mobiles) };
        }
      }

      const byPassportRes = await fetch(`${API_BASE}/api/consignees/by-passport/${encodedId}`);
      if (byPassportRes.ok) {
        const data = await byPassportRes.json();
        return { ...data, mobiles: normalizeMobiles(data?.mobiles) };
      }

      const byNicRes = await fetch(`${API_BASE}/api/consignees/by-nic/${encodedId}`);
      if (byNicRes.ok) {
        const data = await byNicRes.json();
        return { ...data, mobiles: normalizeMobiles(data?.mobiles) };
      }
    } catch (error) {
      console.warn('Failed to fetch consignee details:', error);
    }

    return fallbackPerson(consigneeId);
  };

  const loadCollectionPeople = async (collection) => {
    const [shipperData, consigneeData] = await Promise.all([
      fetchShipperData(collection?.shipperPassportNo),
      fetchConsigneeData(collection?.consigneeId)
    ]);

    adminDebugLog('Collection people loaded:', {
      collectionId: collection?._id || collection?.id,
      shipperPassportNo: collection?.shipperPassportNo,
      consigneeId: collection?.consigneeId,
      shipperMobiles: shipperData?.mobiles,
      consigneeMobiles: consigneeData?.mobiles
    });

    return {
      shipperData: shipperData || fallbackPerson(collection?.shipperPassportNo),
      consigneeData: consigneeData || fallbackPerson(collection?.consigneeId)
    };
  };

  const getCurrentCollectionBoxes = () => {
    if (isEditingCollection) {
      return Array.isArray(editingCollectionData?.boxes) ? editingCollectionData.boxes : [];
    }
    return Array.isArray(selectedCollection?.boxes) ? selectedCollection.boxes : [];
  };

  const getCollectionSummary = (boxes) => {
    return boxes.reduce((acc, box) => {
      const quantity = Number.parseInt(box?.quantity, 10) || 0;
      const cbm = Number.parseFloat(box?.cbm) || 0;
      const unitWeight = Number.parseFloat(box?.weight) || 0;
      const unitPrice = Number.parseFloat(box?.price) || 0;
      const totalWeight = Number.parseFloat(box?.totalWeight);
      const totalPrice = Number.parseFloat(box?.totalPrice);

      acc.totalBoxes += 1;
      acc.totalQty += quantity;
      acc.totalCbm += cbm * quantity;
      acc.totalWeight += Number.isFinite(totalWeight) ? totalWeight : (unitWeight * quantity);
      acc.totalPrice += Number.isFinite(totalPrice) ? totalPrice : (unitPrice * quantity);
      return acc;
    }, {
      totalBoxes: 0,
      totalQty: 0,
      totalCbm: 0,
      totalWeight: 0,
      totalPrice: 0
    });
  };

  const formatAdminDate = (d) => {
    if (!d) return '—';
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return '—';
    return x.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCurrencyRM = (n) => {
    const v = Number(n) || 0;
    return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatOptionalRM = (val) => {
    if (val === '' || val === null || val === undefined) return '-';
    const n = Number(val);
    return Number.isFinite(n) ? formatCurrencyRM(n) : String(val);
  };

  const collectionRowTotalPrice = (c) => {
    const s = getCollectionSummary(c?.boxes || []);
    let lineTotal = s.totalPrice;
    const ch = c?.charges;
    if (ch && typeof ch === 'object') {
      const transportCharge = Number.parseFloat(ch.transportCharge) || 0;
      const packingCharge = Number.parseFloat(ch.packingCharge) || 0;
      const otherCharge = Number.parseFloat(ch.otherCharge) || 0;
      const discountType = ch.discountType === 'amount' ? 'amount' : 'percentage';
      const discountValue = Number.parseFloat(ch.discountValue) || 0;
      const discountAmount = discountType === 'percentage'
        ? (lineTotal * discountValue) / 100
        : discountValue;
      lineTotal = Math.max(0, lineTotal + transportCharge + packingCharge + otherCharge - discountAmount);
    }
    return lineTotal;
  };

  const collectionStatusLabel = (c) => {
    if (c?.lastUpdated) return 'Completed';
    return 'Pending';
  };

  const aggregateStats = useMemo(() => {
    const boxTypeCounts = {};
    let totalPackages = 0;
    let totalCbm = 0;
    let totalWeight = 0;
    let totalRevenue = 0;
    collections.forEach((c) => {
      const s = getCollectionSummary(c?.boxes || []);
      totalPackages += s.totalQty;
      totalCbm += s.totalCbm;
      totalWeight += s.totalWeight;
      let lineTotal = s.totalPrice;
      const ch = c?.charges;
      if (ch && typeof ch === 'object') {
        const transportCharge = Number.parseFloat(ch.transportCharge) || 0;
        const packingCharge = Number.parseFloat(ch.packingCharge) || 0;
        const otherCharge = Number.parseFloat(ch.otherCharge) || 0;
        const discountType = ch.discountType === 'amount' ? 'amount' : 'percentage';
        const discountValue = Number.parseFloat(ch.discountValue) || 0;
        const discountAmount = discountType === 'percentage'
          ? (lineTotal * discountValue) / 100
          : discountValue;
        lineTotal = Math.max(0, lineTotal + transportCharge + packingCharge + otherCharge - discountAmount);
      }
      totalRevenue += lineTotal;
      (c?.boxes || []).forEach((b) => {
        const k = (b.boxType || b.officialBoxName || 'Other').trim() || 'Other';
        boxTypeCounts[k] = (boxTypeCounts[k] || 0) + (Number.parseInt(b.quantity, 10) || 1);
      });
    });
    return {
      totalCollections: collections.length,
      totalPackages,
      totalCbm: Number(totalCbm.toFixed(2)),
      totalWeight: Number(totalWeight.toFixed(2)),
      totalRevenue,
      boxTypeCounts,
    };
  }, [collections]);

  const sortedCollectionsDesc = useMemo(
    () => [...collections].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [collections]
  );

  const filteredCollections = useMemo(() => {
    const q = collectionsFilter.query.trim().toLowerCase();
    const consigneeLabelLower = (c) => {
      const id = c?.consigneeId;
      if (!id) return '';
      const found = consigneesList.find(
        (p) => p._id === id || p.passportNo === id || p.nicNo === id || p.drivingLicenceNo === id
      );
      return String(found?.nameWithInitials || id).toLowerCase();
    };
    const shipperLabelLower = (c) => {
      const passport = c?.shipperPassportNo;
      if (!passport) return '';
      const found = shippersList.find((s) => String(s?.passportNo || '') === String(passport));
      return String(found?.nameWithInitials || passport).toLowerCase();
    };
    return sortedCollectionsDesc.filter((c) => {
      if (collectionsFilter.status === 'pending' && c?.lastUpdated) return false;
      if (collectionsFilter.status === 'completed' && !c?.lastUpdated) return false;
      if (collectionsFilter.clearing !== 'all') {
        const key = String(c?.clearingType || '').trim().toLowerCase().replace(/\s+/g, '_');
        const want = collectionsFilter.clearing;
        const ok =
          (want === 'door_to_door' && (key === 'door_to_door' || key === 'd2d')) ||
          (want === 'gift' && key === 'gift') ||
          (want === 'upb' && key === 'upb');
        if (!ok) return false;
      }
      if (!q) return true;
      return (
        String(c.collectionNo || '').toLowerCase().includes(q) ||
        consigneeLabelLower(c).includes(q) ||
        shipperLabelLower(c).includes(q) ||
        String(c.shipperPassportNo || '').toLowerCase().includes(q) ||
        String(c.consigneeId || '').toLowerCase().includes(q)
      );
    });
  }, [sortedCollectionsDesc, collectionsFilter, consigneesList, shippersList]);

  /** UPB/Gift consolidated parent bills — HBL/shipment only, not regular collections/invoices/receipts */
  const filteredRegularCollections = useMemo(
    () => filteredCollections.filter((c) => !isConsolidatedParent(c)),
    [filteredCollections],
  );

  const stickerEligibleCollections = useMemo(
    () => filteredRegularCollections.filter((c) => canPrintStickers(c, collections).ok),
    [filteredRegularCollections, collections],
  );

  const regularCollectionsCount = useMemo(
    () => collections.filter((c) => !isConsolidatedParent(c)).length,
    [collections],
  );

  const filteredConsolidatedCollections = useMemo(
    () => filteredCollections.filter((c) => isConsolidatedParent(c)),
    [filteredCollections],
  );

  /** HBL tab: regular bills + consolidated parents; hide locked door-to-door children */
  const filteredHblCollections = useMemo(
    () => filteredCollections.filter((c) => !c.consolidatedInto),
    [filteredCollections],
  );

  const hblCollectionsCount = useMemo(
    () => collections.filter((c) => !c.consolidatedInto).length,
    [collections],
  );

  const consolidatedCollectionsCount = useMemo(
    () => collections.filter((c) => isConsolidatedParent(c)).length,
    [collections],
  );

  const collectionsPageSize = 5;
  const collectionsTotalPages = Math.max(1, Math.ceil(filteredRegularCollections.length / collectionsPageSize));
  const pagedCollections = useMemo(() => {
    const start = (collectionsPage - 1) * collectionsPageSize;
    return filteredRegularCollections.slice(start, start + collectionsPageSize);
  }, [filteredRegularCollections, collectionsPage]);

  useEffect(() => {
    setCollectionsPage(1);
  }, [collectionsFilter.query, collectionsFilter.status, collectionsFilter.clearing]);

  useEffect(() => {
    const role = authUser?.role;
    if (!authUser?.isLoggedIn || !authUser?.token || (role !== 'admin' && role !== 'staff')) {
      navigate('/admin/login', { replace: true });
    }
  }, [authUser, navigate]);

  useEffect(() => {
    if (!authUser?.role || !authUser?.token) return;
    if (!canAccessAdminTab(authUser.role, authUser.staffDepartment, activeTab)) {
      setActiveTab(defaultTabForRole(authUser.role, authUser.staffDepartment));
    }
  }, [authUser?.role, authUser?.staffDepartment, authUser?.token, activeTab]);

  useEffect(() => {
    if (!authUser?.requiresMfaSetup || !authUser?.token || mfaLoadRef.current) return;
    mfaLoadRef.current = true;
    (async () => {
      setMfaSetup((s) => ({ ...s, err: '', busy: true }));
      try {
        const res = await fetch(`${API_BASE}/api/auth/admin/mfa/setup/start`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authUser.token}`, 'Content-Type': 'application/json' },
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j.message || 'Could not start authenticator setup');
        setMfaSetup((s) => ({
          ...s,
          qr: j.data?.qrDataUrl || '',
          manualKey: j.data?.manualEntryKey || '',
          busy: false,
        }));
      } catch (e) {
        mfaLoadRef.current = false;
        setMfaSetup((s) => ({ ...s, busy: false, err: e.message || 'Setup failed' }));
      }
    })();
  }, [authUser?.requiresMfaSetup, authUser?.token]);

  const submitMfaSetup = async () => {
    const code = mfaSetup.code.replace(/\s/g, '');
    if (!/^\d{6,8}$/.test(code)) {
      setMfaSetup((s) => ({ ...s, err: 'Enter the 6-digit code from your authenticator app' }));
      return;
    }
    setMfaSetup((s) => ({ ...s, err: '', busy: true, info: '' }));
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin/mfa/setup/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authUser.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Invalid code');
      updateUser({ requiresMfaSetup: false });
      setMfaSetup({ qr: '', manualKey: '', code: '', err: '', busy: false, info: j.message || '' });
      mfaLoadRef.current = false;
    } catch (e) {
      setMfaSetup((s) => ({ ...s, busy: false, err: e.message || 'Verification failed' }));
    }
  };

  const fetchPortalUsers = useCallback(async () => {
    if (!authUser?.token) return;
    setAppUsersLoading(true);
    setAppUsersError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users?limit=200`, {
        headers: { Authorization: `Bearer ${authUser.token}` },
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Failed to load users');
      setAppUsers(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setAppUsersError(e.message || 'Failed to load users');
    } finally {
      setAppUsersLoading(false);
    }
  }, [authUser?.token]);

  useEffect(() => {
    if (activeTab === 'employees' && authUser?.token) {
      fetchPortalUsers();
    }
  }, [activeTab, authUser?.token, fetchPortalUsers]);

  const updatePortalUserRole = async (userId, role) => {
    if (!authUser?.token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Update failed');
      await fetchPortalUsers();
    } catch (e) {
      alert(e.message || 'Could not update role');
    }
  };

  const updatePortalUserDepartment = async (userId, staffDepartment) => {
    if (!authUser?.token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/department`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ staffDepartment }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Update failed');
      await fetchPortalUsers();
    } catch (e) {
      alert(e.message || 'Could not update department');
    }
  };

  const updatePortalUserActive = async (userId, isActive) => {
    if (!authUser?.token) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Update failed');
      await fetchPortalUsers();
    } catch (e) {
      alert(e.message || 'Could not update status');
    }
  };

  const handleCreatePortalUser = async (e) => {
    e.preventDefault();
    if (!authUser?.token) return;
    if (!newPortalUser.name.trim() || !newPortalUser.email.trim() || newPortalUser.password.length < 6) {
      alert('Name, email, and password (min 6 characters) are required.');
      return;
    }
    setCreatingPortalUser(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newPortalUser.name.trim(),
          email: newPortalUser.email.trim(),
          password: newPortalUser.password,
          role: newPortalUser.role,
          staffDepartment: newPortalUser.role === 'staff' ? newPortalUser.staffDepartment : undefined,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || 'Create failed');
      setNewPortalUser({ name: '', email: '', password: '', role: 'customer', staffDepartment: 'full' });
      await fetchPortalUsers();
    } catch (err) {
      alert(err.message || 'Could not create user');
    } finally {
      setCreatingPortalUser(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'settings' || !authUser?.token) return;
    let cancelled = false;
    (async () => {
      setSecurityLoading(true);
      setSecurityMessage({ type: '', text: '' });
      try {
        const { ok, data } = await fetchAuthMe(authUser.token);
        if (!cancelled && ok && data?.data) {
          setSecurityMfaEnabled(Boolean(data.data.totpEnabled));
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setSecurityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, authUser?.token]);

  const handleDisableMfa = async () => {
    if (!authUser?.token) return;
    if (!mfaDisablePassword.trim()) {
      setSecurityMessage({ type: 'err', text: 'Enter your account password to confirm.' });
      return;
    }
    setSecurityLoading(true);
    setSecurityMessage({ type: '', text: '' });
    try {
      const { ok, data } = await adminDisableMfa(authUser.token, mfaDisablePassword);
      if (!ok) {
        setSecurityMessage({ type: 'err', text: data.message || 'Could not disable two-step verification.' });
        return;
      }
      setSecurityMfaEnabled(false);
      setMfaDisablePassword('');
      setSecurityMessage({ type: 'ok', text: data.message || 'Two-step verification is off.' });
    } catch {
      setSecurityMessage({ type: 'err', text: 'Request failed.' });
    } finally {
      setSecurityLoading(false);
    }
  };

  useEffect(() => {
    if (collectionsPage > collectionsTotalPages) {
      setCollectionsPage(collectionsTotalPages);
    }
  }, [collectionsPage, collectionsTotalPages]);

  const donutStyle = useMemo(() => {
    const entries = Object.entries(aggregateStats.boxTypeCounts).sort((a, b) => b[1] - a[1]);
    const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
    const colors = ['#3b82f6', '#f97316', '#ef4444', '#22c55e', '#a855f7', '#14b8a6', '#eab308'];
    let acc = 0;
    const parts = entries.slice(0, 6).map(([key, val], i) => {
      const pct = (val / total) * 100;
      const start = acc;
      acc += pct;
      return `${colors[i % colors.length]} ${start}% ${acc}%`;
    });
    if (parts.length === 0) {
      return { background: 'conic-gradient(#cbd5e1 0% 100%)', legend: [] };
    }
    return {
      background: `conic-gradient(${parts.join(', ')})`,
      legend: entries.slice(0, 6).map(([key, val], i) => ({
        key,
        val,
        color: colors[i % colors.length],
      })),
    };
  }, [aggregateStats.boxTypeCounts]);

  const resetEmptyBoxForm = () => {
    setEmptyBoxForm({
      boxName: '',
      secondName: '',
      widthInch: '',
      lengthInch: '',
      heightInch: '',
      weightKg: '',
      priceUpbGift: '',
      priceDoorToDoor: '',
      quantity: 0,
      description: '',
      boxImage: null,
      boxImageUrl: '',
      status: 'Active'
    });
  };

  const handleEmptyBoxImage = (file) => {
    if (!file) return;
    if (!isLikelyAdminImageFile(file)) {
      alert('Please select an image file (e.g. JPG, PNG, HEIC).');
      return;
    }
    if (file.size > ADMIN_IMAGE_MAX_BYTES) {
      alert('Image size should be less than 10MB.');
      return;
    }
    setEmptyBoxForm((prev) => ({ ...prev, boxImage: file, boxImageUrl: '' }));
  };

  const buildEmptyBoxFormData = () => {
    const formData = new FormData();
    formData.append('boxName', emptyBoxForm.boxName || '');
    formData.append('secondName', emptyBoxForm.secondName || '');
    formData.append('widthInch', emptyBoxForm.widthInch || '');
    formData.append('lengthInch', emptyBoxForm.lengthInch || '');
    formData.append('heightInch', emptyBoxForm.heightInch || '');
    formData.append('weightKg', emptyBoxForm.weightKg || '');
    formData.append('priceUpbGift', emptyBoxForm.priceUpbGift || '');
    formData.append('priceDoorToDoor', emptyBoxForm.priceDoorToDoor || '');
    formData.append('quantity', String(emptyBoxForm.quantity || 0));
    formData.append('description', emptyBoxForm.description || '');
    formData.append('status', emptyBoxForm.status || 'Active');
    if (emptyBoxForm.boxImage instanceof File) {
      formData.append('boxImage', emptyBoxForm.boxImage);
    }
    return formData;
  };

  const fetchEmptyBoxes = useCallback(async () => {
    setLoadingEmptyBoxes(true);
    try {
      const res = await fetch(`${API_BASE}/api/empty-boxes`);
      if (res.status === 404) {
        // Allow older backend instances (or not-yet-restarted server) without hard-failing UI.
        setEmptyBoxes([]);
        return;
      }
      if (!res.ok) {
        throw new Error(`Failed to load empty boxes: ${res.status}`);
      }
      const data = await res.json();
      setEmptyBoxes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to load empty boxes');
    } finally {
      setLoadingEmptyBoxes(false);
    }
  }, []);

  const openEmptyBoxEditor = (box) => {
    setEditingEmptyBoxId(box?._id || '');
    setEmptyBoxForm({
      boxName: box?.boxName || '',
      secondName: box?.secondName || '',
      widthInch: box?.widthInch ?? '',
      lengthInch: box?.lengthInch ?? '',
      heightInch: box?.heightInch ?? '',
      weightKg: box?.weightKg ?? '',
      priceUpbGift: box?.priceUpbGift ?? '',
      priceDoorToDoor: box?.priceDoorToDoor ?? '',
      quantity: box?.quantity ?? 0,
      description: box?.description || '',
      boxImage: null,
      boxImageUrl: box?.boxImageUrl || '',
      status: box?.status || 'Active'
    });
    setShowEmptyBoxModal(true);
  };

  const closeEmptyBoxModal = () => {
    setShowEmptyBoxModal(false);
    setEditingEmptyBoxId('');
    resetEmptyBoxForm();
  };

  const handleAddEmptyBox = async () => {
    if (!emptyBoxForm.boxName.trim()) {
      alert('Box Name is required.');
      return;
    }
    const adminPwd = ensureAdminApiPassword('Enter admin password to save empty box:');
    if (adminPwd == null) {
      alert('Admin password is required.');
      return;
    }
    setSavingEmptyBox(true);
    try {
      const formData = buildEmptyBoxFormData();
      const endpoint = editingEmptyBoxId
        ? `${API_BASE}/api/empty-boxes/${editingEmptyBoxId}`
        : `${API_BASE}/api/empty-boxes`;
      const method = editingEmptyBoxId ? 'PUT' : 'POST';
      const res = await fetch(endpoint, { method, headers: { ...getAdminPasswordHeaders() }, body: formData });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to ${editingEmptyBoxId ? 'update' : 'create'} empty box`);
      }
      await fetchEmptyBoxes();
      closeEmptyBoxModal();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to save empty box');
    } finally {
      setSavingEmptyBox(false);
    }
  };

  const handleDeleteEmptyBox = async (id) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this empty box?')) return;
    const adminPwd = ensureAdminApiPassword('Enter admin password to delete empty box:');
    if (adminPwd == null) {
      return;
    }
    setDeletingEmptyBoxId(id);
    try {
      const res = await fetch(`${API_BASE}/api/empty-boxes/${id}`, {
        method: 'DELETE',
        headers: { ...getAdminPasswordHeaders() },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to delete empty box');
      }
      setEmptyBoxes((prev) => prev.filter((box) => box._id !== id));
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to delete empty box');
    } finally {
      setDeletingEmptyBoxId('');
    }
  };

  const resetBoxItemForm = () => {
    setBoxItemForm({ itemName: '', officialName: '' });
    setEditingBoxItemId('');
  };

  const fetchBoxItems = useCallback(async () => {
    setLoadingBoxItems(true);
    try {
      const res = await fetch(`${API_BASE}/api/box-items`);
      if (res.status === 404) {
        setBoxItems([]);
        return;
      }
      if (!res.ok) {
        throw new Error(`Failed to load box items: ${res.status}`);
      }
      const data = await res.json();
      setBoxItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to load box items');
    } finally {
      setLoadingBoxItems(false);
    }
  }, []);

  const openBoxItemEditor = (item) => {
    setEditingBoxItemId(item?._id || '');
    setBoxItemForm({
      itemName: item?.itemName || '',
      officialName: item?.officialName || ''
    });
  };

  const handleSaveBoxItem = async () => {
    if (!boxItemForm.itemName.trim() || !boxItemForm.officialName.trim()) {
      alert('Item Name and Official Name are required.');
      return;
    }
    const adminPwd = ensureAdminApiPassword('Enter admin password to save box item:');
    if (adminPwd == null) {
      alert('Admin password is required.');
      return;
    }
    setSavingBoxItem(true);
    try {
      const endpoint = editingBoxItemId
        ? `${API_BASE}/api/box-items/${editingBoxItemId}`
        : `${API_BASE}/api/box-items`;
      const method = editingBoxItemId ? 'PUT' : 'POST';
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAdminPasswordHeaders() },
        body: JSON.stringify({
          itemName: boxItemForm.itemName,
          officialName: boxItemForm.officialName
        })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to ${editingBoxItemId ? 'update' : 'create'} item`);
      }
      await fetchBoxItems();
      resetBoxItemForm();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to save item');
    } finally {
      setSavingBoxItem(false);
    }
  };

  const handleDeleteBoxItem = async (id) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    const adminPwd = ensureAdminApiPassword('Enter admin password to delete box item:');
    if (adminPwd == null) return;
    setDeletingBoxItemId(id);
    try {
      const res = await fetch(`${API_BASE}/api/box-items/${id}`, {
        method: 'DELETE',
        headers: { ...getAdminPasswordHeaders() },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to delete item');
      }
      setBoxItems((prev) => prev.filter((item) => item._id !== id));
      setBoxCollectionForm((prev) => ({
        ...prev,
        boxes: prev.boxes.map((box) => (box.boxType && boxItems.some((i) => i._id === id && i.itemName === box.boxType))
          ? { ...box, boxType: '' }
          : box)
      }));
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to delete item');
    } finally {
      setDeletingBoxItemId('');
    }
  };

  const parseMobilesText = (value) =>
    (value || '')
      .split(',')
      .map((m) => m.trim())
      .filter(Boolean);

  const toMobilesText = (mobiles) =>
    Array.isArray(mobiles) ? mobiles.filter(Boolean).join(', ') : (mobiles || '');

  const fetchShippersList = useCallback(async () => {
    setLoadingShippers(true);
    try {
      const res = await fetch(`${API_BASE}/api/shippers`);
      if (res.ok) {
        const data = await res.json();
        setShippersList(Array.isArray(data) ? data : []);
      } else if (res.status === 404) {
        // Fallback for older backend instances that don't yet expose /api/shippers
        const collectionsRes = await fetch(`${API_BASE}/api/collections`);
        if (!collectionsRes.ok) throw new Error(`Failed to load shippers: ${res.status}`);
        const collections = await collectionsRes.json();
        const uniquePassports = [...new Set((collections || []).map((c) => c.shipperPassportNo).filter(Boolean))];
        const shipperResults = await Promise.all(
          uniquePassports.map(async (passportNo) => {
            const byPassportRes = await fetch(`${API_BASE}/api/shippers/by-passport/${encodeURIComponent(passportNo)}`);
            if (!byPassportRes.ok) return null;
            return byPassportRes.json();
          })
        );
        setShippersList(shipperResults.filter(Boolean));
      } else {
        throw new Error(`Failed to load shippers: ${res.status}`);
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setLoadingShippers(false);
    }
  }, []);

  const fetchConsigneesList = useCallback(async () => {
    setLoadingConsignees(true);
    try {
      const res = await fetch(`${API_BASE}/api/consignees`);
      if (res.ok) {
        const data = await res.json();
        setConsigneesList(Array.isArray(data) ? data : []);
      } else if (res.status === 404) {
        // Fallback for older backend instances that don't yet expose /api/consignees
        const collectionsRes = await fetch(`${API_BASE}/api/collections`);
        if (!collectionsRes.ok) throw new Error(`Failed to load consignees: ${res.status}`);
        const collections = await collectionsRes.json();
        const uniqueIds = [...new Set((collections || []).map((c) => c.consigneeId).filter(Boolean))];
        const consigneeResults = await Promise.all(
          uniqueIds.map(async (idValue) => {
            const encoded = encodeURIComponent(idValue);
            if (/^[0-9a-fA-F]{24}$/.test(idValue)) {
              const byIdRes = await fetch(`${API_BASE}/api/consignees/by-id/${encoded}`);
              if (byIdRes.ok) return byIdRes.json();
            }
            const byPassportRes = await fetch(`${API_BASE}/api/consignees/by-passport/${encoded}`);
            if (byPassportRes.ok) return byPassportRes.json();
            const byNicRes = await fetch(`${API_BASE}/api/consignees/by-nic/${encoded}`);
            if (byNicRes.ok) return byNicRes.json();
            return null;
          })
        );
        setConsigneesList(consigneeResults.filter(Boolean));
      } else {
        throw new Error(`Failed to load consignees: ${res.status}`);
      }
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setLoadingConsignees(false);
    }
  }, []);

  const consigneeMatchesId = (consignee, id) => {
    if (!consignee || id == null || id === '') return false;
    const key = String(id).trim();
    return [consignee._id, consignee.passportNo, consignee.nicNo, consignee.drivingLicenceNo]
      .filter(Boolean)
      .some((value) => String(value) === key);
  };

  const findShipperInList = (passportNo) => {
    const key = String(passportNo || '').trim();
    if (!key) return null;
    return shippersList.find((shipper) => String(shipper?.passportNo || '') === key) || null;
  };

  const findConsigneeInList = (consigneeId) => {
    const key = String(consigneeId || '').trim();
    if (!key) return null;
    return consigneesList.find((consignee) => consigneeMatchesId(consignee, key)) || null;
  };

  const viewShipperDetails = async (shipperOrPassport, { closePreview = true } = {}) => {
    const passportKey = typeof shipperOrPassport === 'object'
      ? String(shipperOrPassport?.passportNo || '').trim()
      : String(shipperOrPassport || '').trim();
    if (!passportKey) {
      alert('Shipper passport number is missing.');
      return;
    }
    if (closePreview) {
      setShowCollectionPreview(false);
    }
    setActiveTab('customers');
    setCustomerTab('shippers');
    let shipper = typeof shipperOrPassport === 'object' && shipperOrPassport?.passportNo
      ? shipperOrPassport
      : findShipperInList(passportKey);
    if (!shipper) {
      shipper = await fetchShipperData(passportKey);
    }
    if (!shipper) {
      alert(`Shipper not found for passport: ${passportKey}`);
      return;
    }
    setSelectedShipperCustomer(shipper);
    setCustomerPanelMode('view');
    setCustomersFilter({ query: passportKey });
  };

  const viewConsigneeDetails = async (consigneeOrId, { closePreview = true } = {}) => {
    const idKey = typeof consigneeOrId === 'object'
      ? String(consigneeOrId?.passportNo || consigneeOrId?.nicNo || consigneeOrId?.drivingLicenceNo || consigneeOrId?._id || '').trim()
      : String(consigneeOrId || '').trim();
    if (!idKey) {
      alert('Consignee ID is missing.');
      return;
    }
    if (closePreview) {
      setShowCollectionPreview(false);
    }
    setActiveTab('customers');
    setCustomerTab('consignees');
    let consignee = typeof consigneeOrId === 'object' && (consigneeOrId?.passportNo || consigneeOrId?.nicNo || consigneeOrId?.drivingLicenceNo)
      ? consigneeOrId
      : findConsigneeInList(idKey);
    if (!consignee) {
      consignee = await fetchConsigneeData(idKey);
    }
    if (!consignee || consignee.nameWithInitials === 'N/A') {
      alert(`Consignee not found for ID: ${idKey}`);
      return;
    }
    setSelectedConsigneeCustomer(consignee);
    setCustomerPanelMode('view');
    setCustomersFilter({ query: idKey });
  };

  const openShipperEditor = (shipper) => {
    setCustomerPanelMode('edit');
    setSelectedShipperCustomer(shipper);
    setShipperForm({
      nameWithInitials: shipper?.nameWithInitials || '',
      passportNo: shipper?.passportNo || '',
      address: shipper?.address || '',
      mobilesText: toMobilesText(shipper?.mobiles),
      passportPhoto: null,
      passportPhotoUrl: shipper?.passportPhotoUrl || ''
    });
  };

  const openConsigneeEditor = (consignee) => {
    setCustomerPanelMode('edit');
    setSelectedConsigneeCustomer(consignee);
    setConsigneeForm({
      nameWithInitials: consignee?.nameWithInitials || '',
      idType: consignee?.idType || (consignee?.nicNo ? 'nic' : (consignee?.drivingLicenceNo ? 'driving_licence' : 'passport')),
      passportNo: consignee?.passportNo || '',
      nicNo: consignee?.nicNo || '',
      drivingLicenceNo: consignee?.drivingLicenceNo || '',
      address: consignee?.address || '',
      mobiles: (Array.isArray(consignee?.mobiles) && consignee.mobiles.length > 0) ? consignee.mobiles : [''],
      passportPhoto: null,
      passportPhotoUrl: consignee?.passportPhotoUrl || '',
      drivingLicencePhoto: null,
      drivingLicencePhotoUrl: consignee?.drivingLicencePhotoUrl || '',
      nicFront: null,
      nicFrontUrl: consignee?.nicFrontUrl || '',
      nicBack: null,
      nicBackUrl: consignee?.nicBackUrl || ''
    });
  };

  const resetShipperForm = () => {
    setCustomerPanelMode('add');
    setSelectedShipperCustomer(null);
    setShipperForm({ nameWithInitials: '', passportNo: '', address: '', mobilesText: '', passportPhoto: null, passportPhotoUrl: '' });
  };

  const resetConsigneeForm = () => {
    setCustomerPanelMode('add');
    setSelectedConsigneeCustomer(null);
    setConsigneeForm({
      nameWithInitials: '',
      idType: 'passport',
      passportNo: '',
      nicNo: '',
      drivingLicenceNo: '',
      address: '',
      mobiles: [''],
      passportPhoto: null,
      passportPhotoUrl: '',
      drivingLicencePhoto: null,
      drivingLicencePhotoUrl: '',
      nicFront: null,
      nicFrontUrl: '',
      nicBack: null,
      nicBackUrl: ''
    });
  };

  const handleCustomerImageChoose = (entity, field, file) => {
    if (!file) return;
    if (!isLikelyAdminImageFile(file) || file.size > ADMIN_IMAGE_MAX_BYTES) {
      if (file.size > ADMIN_IMAGE_MAX_BYTES) alert('Image must be 10MB or smaller.');
      else alert('Please choose an image file.');
      return;
    }
    if (entity === 'shipper') {
      setShipperForm((prev) => ({ ...prev, [field]: file }));
      return;
    }
    setConsigneeForm((prev) => ({ ...prev, [field]: file }));
  };

  const saveShipperCustomer = async () => {
    if (!shipperForm.passportNo.trim()) {
      alert('Passport No is required for shipper.');
      return;
    }
    setSavingShipper(true);
    try {
      const fd = new FormData();
      fd.append('nameWithInitials', shipperForm.nameWithInitials || '');
      fd.append('passportNo', shipperForm.passportNo.trim());
      fd.append('address', shipperForm.address || '');
      parseMobilesText(shipperForm.mobilesText).forEach((m, i) => fd.append(`mobiles[${i}]`, m));
      if (shipperForm.passportPhoto instanceof File) fd.append('passportPhoto', shipperForm.passportPhoto);
      const res = await fetch(`${API_BASE}/api/shippers/upsert`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error('Failed to save shipper');
      await fetchShippersList();
      alert(selectedShipperCustomer ? 'Shipper updated successfully.' : 'Shipper added successfully.');
      resetShipperForm();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingShipper(false);
    }
  };

  const saveConsigneeCustomer = async () => {
    if (consigneeForm.idType === 'passport' && !consigneeForm.passportNo.trim()) {
      alert('Passport No is required for consignee.');
      return;
    }
    if (consigneeForm.idType === 'nic' && !consigneeForm.nicNo.trim()) {
      alert('NIC No is required for consignee.');
      return;
    }
    if (consigneeForm.idType === 'driving_licence' && !consigneeForm.drivingLicenceNo.trim()) {
      alert('Driving Licence No is required for consignee.');
      return;
    }
    setSavingConsignee(true);
    try {
      const fd = new FormData();
      fd.append('nameWithInitials', consigneeForm.nameWithInitials || '');
      fd.append('idType', consigneeForm.idType);
      fd.append('passportNo', consigneeForm.passportNo || '');
      fd.append('nicNo', consigneeForm.nicNo || '');
      fd.append('drivingLicenceNo', consigneeForm.drivingLicenceNo || '');
      fd.append('address', consigneeForm.address || '');
      (consigneeForm.mobiles || []).filter(Boolean).forEach((m, i) => fd.append(`mobiles[${i}]`, m));
      if (consigneeForm.passportPhoto instanceof File) fd.append('passportPhoto', consigneeForm.passportPhoto);
      if (consigneeForm.drivingLicencePhoto instanceof File) fd.append('drivingLicencePhoto', consigneeForm.drivingLicencePhoto);
      if (consigneeForm.nicFront instanceof File) fd.append('nicFront', consigneeForm.nicFront);
      if (consigneeForm.nicBack instanceof File) fd.append('nicBack', consigneeForm.nicBack);
      const res = await fetch(`${API_BASE}/api/consignees/upsert`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error('Failed to save consignee');
      await fetchConsigneesList();
      alert(selectedConsigneeCustomer ? 'Consignee updated successfully.' : 'Consignee added successfully.');
      resetConsigneeForm();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSavingConsignee(false);
    }
  };

  useEffect(() => {
    const loadCollectionsTabs = ['dashboard', 'collections', 'boxStickers', 'boxCollection', 'docInvoices', 'docReceipts', 'docHbl', 'consolidateHbl', 'shipments'];
    if (loadCollectionsTabs.includes(activeTab)) {
      fetchCollections();
    }
  }, [activeTab, fetchCollections]);

  useEffect(() => {
    if (activeTab === 'shipments') {
      fetchShippersList();
      fetchConsigneesList();
    }
  }, [activeTab, fetchShippersList, fetchConsigneesList]);

  useEffect(() => {
    if (activeTab === 'customers') {
      fetchShippersList();
      fetchConsigneesList();
    }
  }, [activeTab, fetchShippersList, fetchConsigneesList]);

  useEffect(() => {
    const needPeopleNames = ['dashboard', 'collections', 'docInvoices', 'docReceipts', 'docHbl', 'consolidateHbl'];
    if (needPeopleNames.includes(activeTab)) {
      fetchConsigneesList();
      fetchShippersList();
    }
  }, [activeTab, fetchConsigneesList, fetchShippersList]);

  useEffect(() => {
    if (activeTab === 'emptyBoxes' || activeTab === 'boxCollection' || activeTab === 'boxItems') {
      fetchEmptyBoxes();
    }
  }, [activeTab, fetchEmptyBoxes]);

  useEffect(() => {
    if (activeTab === 'emptyBoxes' || activeTab === 'boxCollection' || activeTab === 'boxItems') {
      fetchBoxItems();
    }
  }, [activeTab, fetchBoxItems]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        topSearchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Handle receipt generation
  const handleCreateReceipt = async (collection) => {
    try {
      setSelectedCollection(collection);
      const { shipperData, consigneeData } = await loadCollectionPeople(collection);
      setSelectedShipper(shipperData);
      setSelectedConsignee(consigneeData);
      setShowReceipt(true);
    } catch (error) {
      console.error('Error creating receipt:', error);
      alert('Failed to create receipt: ' + error.message);
    }
  };

  // Handle receipt close
  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedCollection(null);
    setSelectedShipper(null);
    setSelectedConsignee(null);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleSaveReceiptPayments = async (payload) => {
    const rawId = selectedCollection?._id ?? selectedCollection?.id;
    const collectionId = rawId != null ? String(rawId).trim() : '';
    if (!collectionId) {
      alert('Collection not loaded.');
      throw new Error('Collection not loaded');
    }
    const adminPassword = window.prompt('Enter admin password to save receipt payments:');
    if (adminPassword === null) {
      throw new Error('Cancelled');
    }
    if (!String(adminPassword).trim()) {
      alert('Admin password is required.');
      throw new Error('Missing password');
    }
    try {
      sessionStorage.setItem('jet_admin_api_password', String(adminPassword).trim());
    } catch (_) {}

    const response = await fetch(`${API_BASE}/api/collections/${collectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-password': String(adminPassword).trim(),
      },
      body: JSON.stringify({
        collectionNo: selectedCollection.collectionNo,
        clearingType: selectedCollection.clearingType || 'gift',
        receiptIssuedDate: payload.receiptIssuedDate,
        receiptPayments: payload.receiptPayments,
      }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const msg = errData.message || errData.error || 'Failed to save receipt payments';
      alert(msg);
      throw new Error(msg);
    }

    const updatedCollection = await response.json();
    setSelectedCollection(updatedCollection);
    setCollections((prev) => prev.map((col) => (
      (col._id || col.id) === (updatedCollection._id || updatedCollection.id) ? updatedCollection : col
    )));
    alert('Receipt payments saved.');
  };

  // Handle invoice generation
  const handleCreateInvoice = async (collection) => {
    try {
      setSelectedCollection(collection);
      const { shipperData, consigneeData } = await loadCollectionPeople(collection);
      setSelectedShipper(shipperData);
      setSelectedConsignee(consigneeData);
      setShowInvoice(true);
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Failed to create invoice: ' + error.message);
    }
  };

  // Handle invoice print
  const handlePrintInvoice = () => {
    window.print();
  };

  // Handle invoice close
  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setSelectedCollection(null);
    setSelectedShipper(null);
    setSelectedConsignee(null);
  };

  // Handle HBL generation
  const resolveChildCollectionsForHbl = async (parent) => {
    const childIds = (parent.childCollectionIds || []).map(String);
    if (!childIds.length) return [];
    let pool = collections;
    const missing = childIds.filter((id) => !pool.find((c) => String(c._id || c.id) === id));
    if (missing.length) {
      try {
        const res = await fetch(`${API_BASE}/api/collections`);
        if (res.ok) pool = await res.json();
      } catch {
        /* use in-memory pool */
      }
    }
    return childIds.map((id) => pool.find((c) => String(c._id || c.id) === id)).filter(Boolean);
  };

  const handleCreateHBL = async (collection) => {
    try {
      setSelectedCollection(collection);
      const { shipperData, consigneeData } = await loadCollectionPeople(collection);
      setSelectedShipper(shipperData);
      setSelectedConsignee(consigneeData);
      if (isConsolidatedParent(collection)) {
        setHblChildCollections(await resolveChildCollectionsForHbl(collection));
      } else {
        setHblChildCollections([]);
      }
      setShowHBL(true);
    } catch (error) {
      console.error('Error creating HBL:', error);
      alert('Failed to create HBL: ' + error.message);
    }
  };

  // Handle HBL print
  const handlePrintHBL = () => {
    window.print();
  };

  // Handle HBL close
  const handleCloseHBL = () => {
    setShowHBL(false);
    setSelectedCollection(null);
    setSelectedShipper(null);
    setSelectedConsignee(null);
    setHblChildCollections([]);
  };

  const handleCreateStickers = async (collection) => {
    try {
      const check = canPrintStickers(collection, collections);
      if (!check.ok) {
        alert(check.reason || 'Cannot print stickers for this collection.');
        return;
      }
      let parentCollection = null;
      let childCollections = [];
      const { consigneeData } = await loadCollectionPeople(collection);

      if (isDoorToDoor(collection?.clearingType) && collection.consolidatedInto) {
        parentCollection = collections.find(
          (c) => String(c._id || c.id) === String(collection.consolidatedInto)
        );
      }
      if (isConsolidatedParent(collection)) {
        childCollections = await resolveChildCollectionsForHbl(collection);
      }

      setSelectedCollection(collection);
      setSelectedConsignee(consigneeData);
      setStickerParentCollection(parentCollection || null);
      setStickerChildCollections(childCollections);
      setShowStickers(true);
    } catch (error) {
      console.error('Error opening stickers:', error);
      alert('Failed to open stickers: ' + error.message);
    }
  };

  const handleCloseStickers = () => {
    setShowStickers(false);
    setStickerParentCollection(null);
    setStickerChildCollections([]);
    setSelectedCollection(null);
    setSelectedConsignee(null);
  };

  const ensurePackagesForSticker = useCallback(async (body) => {
    const id = selectedCollection?._id || selectedCollection?.id;
    if (!id) return;
    const res = await fetch(`${API_BASE}/api/collections/${id}/packages/ensure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.message || 'Failed to register packages');
    }
    return res.json();
  }, [selectedCollection]);

  const openConsolidateWizard = (mode = 'create', parent = null) => {
    setConsolidateWizardMode(mode);
    setConsolidateParent(parent);
    setShowConsolidateWizard(true);
  };

  const closeConsolidateWizard = () => {
    setShowConsolidateWizard(false);
    setConsolidateParent(null);
    setConsolidateWizardMode('create');
  };

  // Handle collection view
  const handleViewCollection = async (collection) => {
    try {
      adminDebugLog('Loading collection details for:', collection._id);
      adminDebugLog('Collection boxes:', collection.boxes?.length || 0);
      setSelectedCollection(collection);
      const { shipperData, consigneeData } = await loadCollectionPeople(collection);
      setSelectedShipper(shipperData);
      setSelectedConsignee(consigneeData);
      adminDebugLog('Collection preview mobile values:', {
        shipperMobiles: shipperData?.mobiles,
        consigneeMobiles: consigneeData?.mobiles
      });
      setShowCollectionPreview(true);
      adminDebugLog('Collection preview opened successfully');
    } catch (error) {
      console.error('Error viewing collection:', error);
      alert('Failed to load collection details: ' + error.message);
    }
  };

  // Handle collection preview close
  const handleCloseCollectionPreview = () => {
    setShowCollectionPreview(false);
    setSelectedCollection(null);
    setSelectedShipper(null);
    setSelectedConsignee(null);
    setIsEditingCollection(false);
    setEditingCollectionData(null);
  };

  // Handle edit collection
  const handleEditCollection = () => {
    setIsEditingCollection(true);
    setEditingCollectionData({
      collectionNo: selectedCollection?.collectionNo,
      collectionDate: selectedCollection?.collectionDate ? new Date(selectedCollection.collectionDate).toISOString().slice(0, 10) : '',
      clearingType: selectedCollection?.clearingType,
      shipperPassportNo: selectedCollection?.shipperPassportNo,
      consigneeId: selectedCollection?.consigneeId,
      boxes: selectedCollection?.boxes || []
    });
  };

  const handleEditCollectionFromTable = async (collection) => {
    try {
      setSelectedCollection(collection);
      const { shipperData, consigneeData } = await loadCollectionPeople(collection);
      setSelectedShipper(shipperData);
      setSelectedConsignee(consigneeData);
      setIsEditingCollection(true);
      setEditingCollectionData({
        collectionNo: collection?.collectionNo,
        collectionDate: collection?.collectionDate ? new Date(collection.collectionDate).toISOString().slice(0, 10) : '',
        clearingType: collection?.clearingType,
        shipperPassportNo: collection?.shipperPassportNo,
        consigneeId: collection?.consigneeId,
        boxes: collection?.boxes || []
      });
      setShowCollectionPreview(true);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to open editor');
    }
  };

  // Handle save collection updates
  const handleSaveCollectionUpdates = async () => {
    try {
      const rawId = selectedCollection?._id ?? selectedCollection?.id;
      const collectionId = rawId != null ? String(rawId).trim() : '';
      adminDebugLog('Updating collection:', collectionId);
      adminDebugLog('Selected collection:', selectedCollection);
      adminDebugLog('Update data:', editingCollectionData);

      if (!collectionId) {
        throw new Error('Collection ID not found');
      }
      if (!editingCollectionData) {
        throw new Error('No collection changes to save');
      }

      const adminPassword = window.prompt('Enter admin password to update this collection:');
      if (adminPassword === null) {
        return;
      }
      const trimmedPassword = String(adminPassword).trim();
      if (!trimmedPassword) {
        alert('Admin password is required to update collection.');
        return;
      }
      try {
        sessionStorage.setItem('jet_admin_api_password', trimmedPassword);
      } catch (_) {}

      const shipperPassportNo = String(editingCollectionData.shipperPassportNo || '').trim();
      const consigneeId = String(editingCollectionData.consigneeId || '').trim();

      await Promise.all([
        upsertShipperFromCollectionEdit(selectedShipper, shipperPassportNo),
        upsertConsigneeFromCollectionEdit(selectedConsignee, consigneeId),
      ]);

      const collectionPayload = {
        collectionNo: editingCollectionData.collectionNo,
        collectionDate: editingCollectionData.collectionDate || undefined,
        clearingType: editingCollectionData.clearingType,
        shipperPassportNo,
        consigneeId,
        boxes: Array.isArray(editingCollectionData.boxes) ? editingCollectionData.boxes : [],
      };

      const response = await fetch(`${API_BASE}/api/collections/${collectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': trimmedPassword,
        },
        body: JSON.stringify(collectionPayload),
      });

      adminDebugLog('Response status:', response.status);
      adminDebugLog('Response ok:', response.ok);

      if (response.ok) {
        const updatedCollection = await response.json();
        adminDebugLog('Updated collection:', updatedCollection);
        const updatedId = String(updatedCollection?._id || updatedCollection?.id || collectionId);
        setSelectedCollection(updatedCollection);

        setCollections((prev) => prev.map((col) => (
          String(col._id || col.id) === updatedId ? updatedCollection : col
        )));

        const { shipperData, consigneeData } = await loadCollectionPeople(updatedCollection);
        setSelectedShipper(shipperData);
        setSelectedConsignee(consigneeData);

        setIsEditingCollection(false);
        setEditingCollectionData(null);
        alert('Collection updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to update collection');
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      
      // Try to provide more specific error information
      let errorMessage = 'Failed to update collection: ' + error.message;
      
      if (error.message.includes('Collection ID not found')) {
        errorMessage = 'Collection ID not found. Please refresh the page and try again.';
      } else if (error.message.includes('Invalid collection ID format')) {
        errorMessage = 'Invalid collection ID format. Please refresh the page and try again.';
      } else if (error.message.includes('Collection not found')) {
        errorMessage = 'Collection not found in database. It may have been deleted.';
      } else if (error.message.includes('Invalid admin password')) {
        errorMessage = 'Invalid admin password. Collection update was blocked.';
      } else if (error.message.includes('not configured on server')) {
        errorMessage = 'Server admin password is not configured. Please set ADMIN_COLLECTION_EDIT_PASSWORD in backend .env.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditingCollection(false);
    setEditingCollectionData(null);
  };

  // Handle delete collection
  const handleDeleteCollection = async (collectionId) => {
    if (!collectionId) {
      alert('Collection ID not found. Please refresh and try again.');
      return;
    }
    // Find the collection to show details in confirmation
    const collection = collections.find(col => (col._id || col.id) === collectionId);
    const collectionNo = collection?.collectionNo || 'Unknown';
    
    if (!window.confirm(`Are you sure you want to delete collection "${collectionNo}"?\n\nThis action cannot be undone and will permanently remove:\n- Collection details\n- Associated shipper data\n- Associated consignee data\n- All box information`)) {
      return;
    }

    setDeletingCollection(collectionId);
    try {
      const adminPwd = window.prompt('Enter admin password to delete this collection:');
      if (adminPwd === null) {
        setDeletingCollection(null);
        return;
      }
      if (!String(adminPwd).trim()) {
        alert('Admin password is required to delete a collection.');
        setDeletingCollection(null);
        return;
      }
      try {
        sessionStorage.setItem('jet_admin_api_password', String(adminPwd).trim());
      } catch (_) {}

      adminDebugLog('Deleting collection:', collectionId);
      const response = await fetch(`${API_BASE}/api/collections/${collectionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminPasswordHeaders(),
        }
      });

      adminDebugLog('Delete response status:', response.status);

      if (response.ok) {
        // Remove from collections list
        setCollections(prev => prev.filter(col => (col._id || col.id) !== collectionId));
        if ((selectedCollection?._id || selectedCollection?.id) === collectionId) {
          handleCloseCollectionPreview();
        }
        alert(`Collection "${collectionNo}" deleted successfully!`);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert('Failed to delete collection: ' + error.message);
    } finally {
      setDeletingCollection(null);
    }
  };

  // Handle add new box
  const handleAddBox = () => {
    const newBox = {
      boxType: '',
      officialBoxName: '',
      d2d: '',
      description: '',
      dimensionsUnit: 'inch',
      length: '',
      width: '',
      height: '',
      cbm: '',
      totalCbm: '',
      weight: '',
      price: '',
      quantity: 1
    };
    setEditingCollectionData({
      ...editingCollectionData,
      boxes: [...editingCollectionData.boxes, newBox]
    });
  };

  // Handle remove box
  const handleRemoveBox = (index) => {
    const updatedBoxes = editingCollectionData.boxes.filter((_, i) => i !== index);
    setEditingCollectionData({
      ...editingCollectionData,
      boxes: updatedBoxes
    });
  };

  // Handle update box
  const handleUpdateBox = (index, field, value) => {
    const updatedBoxes = [...editingCollectionData.boxes];
    updatedBoxes[index] = { ...updatedBoxes[index], [field]: value };
    
    // Auto-calculate CBM if dimensions change
    if (['length', 'width', 'height', 'dimensionsUnit'].includes(field)) {
      const unit = updatedBoxes[index].dimensionsUnit;
      const cbmStr = computeCbmFromDimensions(
        unit,
        updatedBoxes[index].length,
        updatedBoxes[index].width,
        updatedBoxes[index].height
      );
      if (cbmStr) updatedBoxes[index].cbm = cbmStr;
    }
    
    setEditingCollectionData({
      ...editingCollectionData,
      boxes: updatedBoxes
    });
  };

  // Search for existing shipper by passport number
  const searchShipper = async (passportNo) => {
    if (!passportNo.trim()) {
      alert('Please enter a passport number to search');
      return;
    }
    
    setSearchingShipper(true);
    try {
      adminDebugLog('Searching for shipper with passport:', passportNo);
      const res = await fetch(`${API_BASE}/api/shippers/by-passport/${encodeURIComponent(passportNo)}`);
      
      if (res.ok) {
        const shipperData = await res.json();
        adminDebugLog('Found shipper:', shipperData);
        
        if (shipperData) {
          // Auto-fill shipper data
          setBoxCollectionForm(prev => ({
            ...prev,
            shipper: {
              ...prev.shipper,
              nameWithInitials: shipperData.nameWithInitials || '',
              passportNo: shipperData.passportNo || '',
              address: shipperData.address || '',
              mobiles: shipperData.mobiles && shipperData.mobiles.length > 0 ? shipperData.mobiles : [''],
              passportPhoto: shipperData.passportPhotoUrl || null,
            }
          }));
          alert('Shipper data auto-filled successfully!');
        } else {
          alert('No shipper found with this passport number');
        }
      } else if (res.status === 404) {
        alert('No shipper found with this passport number');
      } else {
        const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Shipper search error response:', errorData);
        alert('Error searching for shipper: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Shipper search error:', error);
      alert('Failed to search for shipper: ' + error.message);
    } finally {
      setSearchingShipper(false);
    }
  };

  // Search for existing consignee by passport or NIC number
  const searchConsignee = async (idNumber) => {
    if (!idNumber.trim()) {
      alert('Please enter a passport or NIC number to search');
      return;
    }
    
    setSearchingConsignee(true);
    try {
      adminDebugLog('Searching for consignee with ID:', idNumber);
      
      // Try passport first, then NIC
      let res = await fetch(`${API_BASE}/api/consignees/by-passport/${encodeURIComponent(idNumber)}`);
      let consigneeData = null;
      
      if (res.ok) {
        consigneeData = await res.json();
      } else if (res.status === 404) {
        // Try NIC if passport search returned 404
        res = await fetch(`${API_BASE}/api/consignees/by-nic/${encodeURIComponent(idNumber)}`);
        if (res.ok) {
          consigneeData = await res.json();
        } else if (res.status === 404) {
          // Both searches returned 404, no consignee found
          consigneeData = null;
        } else {
          // Other error
          const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(`Consignee search error: ${errorData.message || 'Unknown error'}`);
        }
      } else {
        // Other error
        const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(`Consignee search error: ${errorData.message || 'Unknown error'}`);
      }
      
      if (consigneeData) {
        adminDebugLog('Found consignee:', consigneeData);
        
        // Auto-fill consignee data
        setBoxCollectionForm(prev => ({
          ...prev,
          consignee: {
            ...prev.consignee,
            nameWithInitials: consigneeData.nameWithInitials || '',
            idType: consigneeData.idType || 'passport',
            passportNo: consigneeData.passportNo || '',
            nicNo: consigneeData.nicNo || '',
            drivingLicenceNo: consigneeData.drivingLicenceNo || '',
            address: consigneeData.address || '',
            mobiles: consigneeData.mobiles && consigneeData.mobiles.length > 0 ? consigneeData.mobiles : [''],
            passportPhoto: consigneeData.passportPhotoUrl || null,
            drivingLicencePhoto: consigneeData.drivingLicencePhotoUrl || null,
            nicFront: consigneeData.nicFrontUrl || null,
            nicBack: consigneeData.nicBackUrl || null,
          }
        }));
        alert('Consignee data auto-filled successfully!');
      } else {
        alert('No consignee found with this ID number');
      }
    } catch (error) {
      console.error('Consignee search error:', error);
      alert('Failed to search for consignee: ' + error.message);
    } finally {
      setSearchingConsignee(false);
    }
  };

  const buildPersonFormData = (type) => {
    const person = type === 'shipper' ? boxCollectionForm.shipper : boxCollectionForm.consignee;
    const fd = new FormData();
    fd.append('nameWithInitials', person.nameWithInitials || '');
    fd.append('address', person.address || '');
    person.mobiles.filter(Boolean).forEach((m, i) => fd.append(`mobiles[${i}]`, m));
    adminDebugLog(`${type} upsert mobiles payload:`, person.mobiles.filter(Boolean));
    if (type === 'shipper') {
      fd.append('passportNo', person.passportNo || '');
      if (person.passportPhoto instanceof File) fd.append('passportPhoto', person.passportPhoto);
    } else {
      fd.append('idType', person.idType);
      if (person.idType === 'passport') fd.append('passportNo', person.passportNo || '');
      if (person.idType === 'nic') fd.append('nicNo', person.nicNo || '');
      if (person.idType === 'driving_licence') fd.append('drivingLicenceNo', person.drivingLicenceNo || '');
      if (person.passportPhoto instanceof File) fd.append('passportPhoto', person.passportPhoto);
      if (person.drivingLicencePhoto instanceof File) fd.append('drivingLicencePhoto', person.drivingLicencePhoto);
      if (person.nicFront instanceof File) fd.append('nicFront', person.nicFront);
      if (person.nicBack instanceof File) fd.append('nicBack', person.nicBack);
    }
    return fd;
  };

  const handleSubmitCollection = async () => {
    adminDebugLog('=== SUBMIT COLLECTION CLICKED ===');
    adminDebugLog('API_BASE:', API_BASE);
    adminDebugLog('Current form data:', boxCollectionForm);

    if (!boxCollectionForm.collectionNo?.trim()) {
      alert('Collection No is required and must be unique.');
      return;
    }
    if (!boxCollectionForm.clearingType) {
      alert('Please select Clearing Type.');
      return;
    }
    if (!boxCollectionForm.consignee.idType) {
      alert('Please select Consignee ID Type.');
      return;
    }
    
    setIsSubmittingCollection(true);
    try {
      adminDebugLog('Starting collection submission...');
      
      // Save shipper and consignee in their databases
      const shipperFd = buildPersonFormData('shipper');
      const consigneeFd = buildPersonFormData('consignee');
      
      adminDebugLog('Shipper FormData:', shipperFd);
      adminDebugLog('Consignee FormData:', consigneeFd);
      
      adminDebugLog('Saving shipper and consignee...');
      const [shipperResult, consigneeResult] = await Promise.all([
        upsertPerson('shipper', shipperFd),
        upsertPerson('consignee', consigneeFd)
      ]);
      
      adminDebugLog('Shipper saved:', shipperResult);
      adminDebugLog('Consignee saved:', consigneeResult);
      
      // Submit collection payload (boxes etc.)
      adminDebugLog('Submitting collection...');
      const collectionPayload = {
        ...boxCollectionForm,
        shipperPassportNo: boxCollectionForm.shipper.passportNo,
        consigneeId: boxCollectionForm.consignee.idType === 'nic'
          ? boxCollectionForm.consignee.nicNo
          : boxCollectionForm.consignee.idType === 'driving_licence'
            ? boxCollectionForm.consignee.drivingLicenceNo
            : boxCollectionForm.consignee.passportNo
      };
      adminDebugLog('Collection payload:', collectionPayload);
      
      const collectionRes = await fetch(`${API_BASE}/api/collections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(collectionPayload)
      });
      
      adminDebugLog('Collection response status:', collectionRes.status);
      adminDebugLog('Collection response ok:', collectionRes.ok);
      
      if (!collectionRes.ok) {
        const errorText = await collectionRes.text();
        console.error('Collection error response:', errorText);
        throw new Error(`Collection failed: ${collectionRes.status} - ${errorText}`);
      }
      
      const collectionResult = await collectionRes.json();
      adminDebugLog('Collection saved:', collectionResult);
      
      setShowBoxPreview(false);
      // Reset form to a fresh state
      setBoxCollectionForm({
        collectionNo: '',
        collectionDate: getTodayYmd(),
        clearingType: '',
        boxDimensionUnit: 'inch',
        shipper: { nameWithInitials: '', passportNo: '', address: '', mobiles: [''], passportPhoto: null },
        consignee: { nameWithInitials: '', idType: '', passportNo: '', nicNo: '', drivingLicenceNo: '', address: '', mobiles: [''], passportPhoto: null, drivingLicencePhoto: null, nicFront: null, nicBack: null },
        charges: { transportCharge: '', packingCharge: '', otherCharge: '', discountType: 'percentage', discountValue: '' },
        boxes: [ { boxType: '', officialBoxName: '', d2d: '', description: '', dimensionsUnit: 'inch', length: '', width: '', height: '', cbm: '', totalCbm: '', weight: '', totalWeight: '', price: '', totalPrice: '', quantity: 1 } ]
      });
      
      // Refresh collections list if we're on the collections tab
      if (activeTab === 'collections') {
        fetchCollections();
      }
      
      // Navigate to Collections tab to show the submitted collection
      setActiveTab('collections');
      alert('Collection submitted successfully!');
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error stack:', error.stack);
      alert('Failed to submit collection: ' + error.message);
    } finally {
      setIsSubmittingCollection(false);
    }
  };

  const updateMobile = (section, idx, value) => {
    setBoxCollectionForm(prev => {
      const mobiles = [...prev[section].mobiles];
      mobiles[idx] = value;
      return { ...prev, [section]: { ...prev[section], mobiles } };
    });
  };

  const handleFile = (path, file) => {
    if (!file) return;
    if (!isLikelyAdminImageFile(file)) {
      alert('Please choose an image file (e.g. JPG, PNG, HEIC).');
      return;
    }
    if (file.size > ADMIN_IMAGE_MAX_BYTES) {
      alert('Image must be 10MB or smaller.');
      return;
    }
    updateBoxForm(path, file);
  };

  const getPreviewUrl = (file, cacheKey = '') => {
    if (!file) return '';
    if (typeof file === 'string') {
      const resolved = /^https?:\/\//i.test(file)
        ? file
        : file.startsWith('/')
          ? `${API_BASE}${file}`
          : file;
      if (cacheKey) {
        const joiner = resolved.includes('?') ? '&' : '?';
        return `${resolved}${joiner}v=${encodeURIComponent(cacheKey)}`;
      }
      return resolved;
    }
    try { return URL.createObjectURL(file); } catch { return ''; }
  };

  const addBoxRow = () => {
    setBoxCollectionForm(prev => ({
      ...prev,
      boxes: [
        ...prev.boxes,
        { boxType: '', officialBoxName: '', d2d: '', description: '', dimensionsUnit: prev.boxDimensionUnit || 'cm', length: '', width: '', height: '', cbm: '', totalCbm: '', weight: '', totalWeight: '', price: '', totalPrice: '', quantity: 1 }
      ]
    }));
  };

  const convertDimensionBetweenUnits = (value, from, to) => {
    if (value === '' || value === null || value === undefined) return '';
    const n = parseFloat(value);
    if (Number.isNaN(n)) return value;
    if (from === to) return value;
    const c = 2.54;
    const conv = from === 'cm' && to === 'inch' ? (n / c) : (n * c);
    return conv.toFixed(2);
  };

  const applyRowTotals = (row) => {
    const qty = parseFloat(row.quantity) || 0;
    const unitCbmRaw = row.cbm;
    const unitCbm = parseFloat(unitCbmRaw);
    const unitWeight = parseFloat(row.weight) || 0;
    const unitPrice = parseFloat(row.price) || 0;
    return {
      ...row,
      totalCbm: qty > 0 && unitCbmRaw !== '' && Number.isFinite(unitCbm) ? (qty * unitCbm).toFixed(2) : '',
      totalWeight: qty && unitWeight ? (qty * unitWeight).toFixed(2) : '',
      totalPrice: qty && unitPrice ? (qty * unitPrice).toFixed(2) : ''
    };
  };

  const getBoxPreset = (boxType) => {
    // Presets in centimeters from Pricing page
    const presets = {
      'JET G BOX': { unit: 'cm', length: 32, width: 32, height: 25, cbm: 0.025 },
      'JET C BOX': { unit: 'cm', length: 32, width: 32, height: 50, cbm: 0.050 },
      'JET S BOX': { unit: 'cm', length: 69, width: 38, height: 38, cbm: 0.100 },
      'JET B BOX': { unit: 'cm', length: 46, width: 46, height: 71, cbm: 0.149 },
      'JET M BOX': { unit: 'cm', length: 69, width: 47, height: 76, cbm: 0.246 },
      'JET L BOX': { unit: 'cm', length: 97, width: 69, height: 74, cbm: 0.488 }
    };
    return presets[boxType] || null;
  };

  const getBoxTypeOptions = () => {
    const items = Array.isArray(boxItems)
      ? boxItems.map((item) => ({
          value: item.itemName,
          label: `${item.itemName}${item.officialName ? ` (${item.officialName})` : ''}`,
          source: 'item'
        }))
      : [];

    const emptyBoxOptions = Array.isArray(emptyBoxes)
      ? emptyBoxes
          .filter((box) => box?.boxName)
          .map((box) => ({
            value: box.boxName,
            label: `${box.boxName}${box.secondName ? ` (${box.secondName})` : ''}`,
            source: 'emptyBox'
          }))
      : [];

    const merged = [...items, ...emptyBoxOptions];
    const seen = new Set();
    return merged.filter((opt) => {
      const key = (opt.value || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const updateBoxRow = (index, key, value) => {
    setBoxCollectionForm(prev => {
      const boxes = [...prev.boxes];
      const updated = { ...boxes[index], [key]: value };
      // When changing unit, convert current dimensions to the new unit for display
      if (key === 'dimensionsUnit') {
        const from = boxes[index].dimensionsUnit;
        const to = value;
        const convert = (numStr) => {
          if (numStr === '' || numStr === null || numStr === undefined) return '';
          const n = parseFloat(numStr);
          if (isNaN(n)) return numStr;
          if (from === to) return numStr;
          const c = 2.54;
          const conv = from === 'cm' && to === 'inch' ? (n / c) : (n * c);
          return conv.toFixed(2);
        };
        updated.length = convert(boxes[index].length);
        updated.width = convert(boxes[index].width);
        updated.height = convert(boxes[index].height);
      }
      if (key === 'boxType') {
        const preset = getBoxPreset(value);
        const selectedItem = boxItems.find((item) => (item.itemName || '').trim().toLowerCase() === (value || '').trim().toLowerCase());
        const selectedEmptyBox = emptyBoxes.find((box) => (box.boxName || '').trim().toLowerCase() === (value || '').trim().toLowerCase());
        if (preset) {
          updated.dimensionsUnit = preset.unit;
          updated.length = String(preset.length);
          updated.width = String(preset.width);
          updated.height = String(preset.height);
          updated.cbm = preset.cbm.toFixed(2);
        }
        if (selectedEmptyBox) {
          updated.dimensionsUnit = 'inch';
          updated.length = selectedEmptyBox.lengthInch !== undefined && selectedEmptyBox.lengthInch !== null ? String(selectedEmptyBox.lengthInch) : updated.length;
          updated.width = selectedEmptyBox.widthInch !== undefined && selectedEmptyBox.widthInch !== null ? String(selectedEmptyBox.widthInch) : updated.width;
          updated.height = selectedEmptyBox.heightInch !== undefined && selectedEmptyBox.heightInch !== null ? String(selectedEmptyBox.heightInch) : updated.height;
          updated.weight = selectedEmptyBox.weightKg !== undefined && selectedEmptyBox.weightKg !== null ? String(selectedEmptyBox.weightKg) : updated.weight;
          if (prev.clearingType === 'door_to_door') {
            updated.price = selectedEmptyBox.priceDoorToDoor !== undefined && selectedEmptyBox.priceDoorToDoor !== null ? String(selectedEmptyBox.priceDoorToDoor) : updated.price;
          } else {
            updated.price = selectedEmptyBox.priceUpbGift !== undefined && selectedEmptyBox.priceUpbGift !== null ? String(selectedEmptyBox.priceUpbGift) : updated.price;
          }
        }
        if (selectedItem?.officialName) {
          updated.officialBoxName = selectedItem.officialName;
        } else if (selectedEmptyBox?.secondName) {
          updated.officialBoxName = selectedEmptyBox.secondName;
        }
        // Do not auto-fill description from item type; user enters it manually.
      }
      // auto compute unit CBM whenever dimensions/unit/box type changes
      if (['length','width','height','dimensionsUnit','boxType'].includes(key)) {
        updated.cbm = computeCbm(updated.dimensionsUnit, updated.length, updated.width, updated.height);
      }
      boxes[index] = applyRowTotals(updated);
      return { ...prev, boxes };
    });
  };

  const handleChangeBoxDimensionUnit = (newUnit) => {
    setBoxCollectionForm((prev) => {
      const fromUnit = prev.boxDimensionUnit || 'cm';
      const boxes = prev.boxes.map((box) => {
        const converted = {
          ...box,
          dimensionsUnit: newUnit,
          length: convertDimensionBetweenUnits(box.length, fromUnit, newUnit),
          width: convertDimensionBetweenUnits(box.width, fromUnit, newUnit),
          height: convertDimensionBetweenUnits(box.height, fromUnit, newUnit)
        };
        converted.cbm = computeCbm(converted.dimensionsUnit, converted.length, converted.width, converted.height);
        return applyRowTotals(converted);
      });
      return {
        ...prev,
        boxDimensionUnit: newUnit,
        boxes
      };
    });
  };

  const removeBoxRow = (index) => {
    setBoxCollectionForm(prev => {
      const boxes = prev.boxes.filter((_, i) => i !== index);
      return { ...prev, boxes: boxes.length ? boxes : prev.boxes };
    });
  };

  const getBoxTotals = () => {
    const totals = boxCollectionForm.boxes.reduce((acc, b) => {
      const qty = parseFloat(b.quantity) || 0;
      const cbm = parseFloat(b.cbm) || 0;
      const totalCbm = parseFloat(b.totalCbm);
      const weight = parseFloat(b.totalWeight) || ((parseFloat(b.weight) || 0) * qty);
      const price = parseFloat(b.totalPrice) || ((parseFloat(b.price) || 0) * qty);
      acc.totalQty += qty;
      acc.totalCbm += Number.isFinite(totalCbm) ? totalCbm : (cbm * qty);
      acc.totalWeight += weight;
      acc.totalPrice += price;
      return acc;
    }, { totalQty: 0, totalCbm: 0, totalWeight: 0, totalPrice: 0 });
    totals.totalCbm = Number(totals.totalCbm.toFixed(2));
    return totals;
  };

  const getCollectionPricingSummary = () => {
    const totals = getBoxTotals();
    const subTotal = Number(totals.totalPrice) || 0;
    const transportCharge = Number.parseFloat(boxCollectionForm.charges?.transportCharge) || 0;
    const packingCharge = Number.parseFloat(boxCollectionForm.charges?.packingCharge) || 0;
    const otherCharge = Number.parseFloat(boxCollectionForm.charges?.otherCharge) || 0;
    const discountType = boxCollectionForm.charges?.discountType || 'percentage';
    const discountValue = Number.parseFloat(boxCollectionForm.charges?.discountValue) || 0;
    const discountAmount = discountType === 'percentage'
      ? (subTotal * discountValue) / 100
      : discountValue;

    const totalPrice = Math.max(0, subTotal + transportCharge + packingCharge + otherCharge - discountAmount);

    return {
      ...totals,
      subTotal,
      transportCharge,
      packingCharge,
      otherCharge,
      discountType,
      discountValue,
      discountAmount,
      totalPrice
    };
  };

  const filteredEmptyBoxes = useMemo(() => {
    const q = emptyBoxesFilter.query.trim().toLowerCase();
    return emptyBoxes.filter((box) => {
      const st = String(box.status || 'Active');
      if (emptyBoxesFilter.status !== 'all' && st !== emptyBoxesFilter.status) return false;
      if (!q) return true;
      return (
        String(box.boxName || '').toLowerCase().includes(q) ||
        String(box.secondName || '').toLowerCase().includes(q) ||
        String(box.description || '').toLowerCase().includes(q)
      );
    });
  }, [emptyBoxes, emptyBoxesFilter]);

  const filteredBoxItems = useMemo(() => {
    const q = boxItemsFilter.query.trim().toLowerCase();
    if (!q) return boxItems;
    return boxItems.filter(
      (item) =>
        String(item.itemName || '').toLowerCase().includes(q) ||
        String(item.officialName || '').toLowerCase().includes(q)
    );
  }, [boxItems, boxItemsFilter.query]);

  const filteredAppUsers = useMemo(() => {
    const q = usersFilter.query.trim().toLowerCase();
    return appUsers.filter((u) => {
      if (usersFilter.role !== 'all' && u.role !== usersFilter.role) return false;
      if (usersFilter.active === 'active' && !u.isActive) return false;
      if (usersFilter.active === 'inactive' && u.isActive) return false;
      if (!q) return true;
      return (
        String(u.name || '').toLowerCase().includes(q) ||
        String(u.email || '').toLowerCase().includes(q)
      );
    });
  }, [appUsers, usersFilter]);

  const filteredShippersList = useMemo(() => {
    const q = customersFilter.query.trim().toLowerCase();
    if (!q) return shippersList;
    return shippersList.filter(
      (s) =>
        String(s.nameWithInitials || '').toLowerCase().includes(q) ||
        String(s.passportNo || '').toLowerCase().includes(q) ||
        String(s.address || '').toLowerCase().includes(q)
    );
  }, [shippersList, customersFilter.query]);

  const filteredConsigneesList = useMemo(() => {
    const q = customersFilter.query.trim().toLowerCase();
    if (!q) return consigneesList;
    return consigneesList.filter(
      (c) =>
        String(c.nameWithInitials || '').toLowerCase().includes(q) ||
        String(c.passportNo || '').toLowerCase().includes(q) ||
        String(c.nicNo || '').toLowerCase().includes(q) ||
        String(c.drivingLicenceNo || '').toLowerCase().includes(q) ||
        String(c.address || '').toLowerCase().includes(q)
    );
  }, [consigneesList, customersFilter.query]);

  const clearingTypeLabel = (raw) => {
    if (!raw) return '—';
    const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
    if (s === 'door_to_door' || s === 'd2d') return 'Door to Door';
    if (s === 'gift') return 'Gift';
    if (s === 'upb') return 'UPB';
    return String(raw);
  };

  const consigneeNameForCollection = (c) => {
    const id = c?.consigneeId;
    if (!id) return '—';
    const found = consigneesList.find((p) => consigneeMatchesId(p, id));
    return found?.nameWithInitials || id;
  };

  const idTypeLabel = (idType) => {
    if (idType === 'nic') return 'NIC';
    if (idType === 'driving_licence') return 'Driving Licence';
    return 'Passport';
  };

  const consigneeIdLabel = (consignee) => {
    if (!consignee) return '—';
    if (consignee.idType === 'nic') return consignee.nicNo || '—';
    if (consignee.idType === 'driving_licence') return consignee.drivingLicenceNo || '—';
    return consignee.passportNo || consignee.nicNo || consignee.drivingLicenceNo || '—';
  };

  const shipperNameForCollection = (c) => {
    const passport = c?.shipperPassportNo;
    if (!passport) return '—';
    const found = findShipperInList(passport);
    return found?.nameWithInitials || passport;
  };

  const renderShipperCollectionCell = (collection) => {
    if (!collection?.shipperPassportNo) return '—';
    return (
      <button
        type="button"
        className="jet-person-link"
        onClick={() => viewShipperDetails(collection.shipperPassportNo)}
      >
        {shipperNameForCollection(collection)}
      </button>
    );
  };

  const performLogout = () => {
    clearAdminApiPassword();
    authLogout();
    navigate('/admin/login', { replace: true });
  };

  const paginationNums = useMemo(() => {
    const t = collectionsTotalPages;
    const p = collectionsPage;
    if (t <= 9) return Array.from({ length: t }, (_, i) => i + 1);
    const set = new Set([1, t, p, p - 1, p + 1, p - 2, p + 2].filter((n) => n >= 1 && n <= t));
    return [...set].sort((a, b) => a - b);
  }, [collectionsTotalPages, collectionsPage]);

  const pageMeta = {
    dashboard: { title: 'Dashboard', subtitle: 'Overview across collections, finance, shipments, people, and inventory.' },
    collections: { title: 'Collections', subtitle: 'Manage all cargo collections.' },
    emptyBoxes: { title: 'Empty Boxes', subtitle: 'Configure box types, pricing, and stock.' },
    boxItems: { title: 'Items', subtitle: 'Item names used in collection box types.' },
    customers: { title: 'Shippers / Consignees', subtitle: 'Manage shipper and consignee records and documents.' },
    employees: { title: 'User management', subtitle: 'Portal accounts — customers, staff, and administrators.' },
    reports: { title: 'Reports', subtitle: 'Analytics and exports.' },
    docInvoices: { title: 'Invoices', subtitle: 'Open invoice documents from collections.' },
    docReceipts: { title: 'Receipts', subtitle: 'Open receipts from collections.' },
    docHbl: { title: 'HBL', subtitle: 'House bills of lading from collections.' },
    consolidateHbl: { title: 'D2D → UPB/Gift', subtitle: 'Combine door-to-door bills into consolidated customs bills.' },
    cbmCalculator: { title: 'CBM Calculator', subtitle: 'Calculate cubic meter volume from carton dimensions.' },
    settings: { title: 'Settings', subtitle: 'Appearance, security, and additional tools.' },
    boxCollection: { title: 'Add Collection', subtitle: 'Create a new cargo collection.' },
    shipments: { title: 'Shipments', subtitle: 'Sea freight workspace: details, manifest, packing list, and linked collections.' },
    shippingAgents: { title: 'Shipping agents', subtitle: 'Directory of overseas agents — used on manifests and shipment workspace.' },
    finance: { title: 'Income & expenses', subtitle: 'Collection income, expense tracking, and category management.' },
    reviews: { title: 'Customer reviews', subtitle: 'Approve 5-star reviews with HBL and photos for the public homepage.' },
    enquiries: { title: 'Enquiries', subtitle: 'Website contact form submissions — respond and mark resolved.' },
    packageScan: { title: 'Package scan', subtitle: 'Scan barcodes or enter package codes to update warehouse tracking.' },
    boxStickers: { title: 'Box Stickers', subtitle: 'Print A6 QR/barcode stickers for collection packages.' },
  };

  const mainNavSections = useMemo(
    () => filterNavSections(
      [
    {
      title: 'Overview',
      items: [
        { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
      ],
    },
    {
      title: 'Collections',
      items: [
        { id: 'collections', label: 'Collections', Icon: FileText },
        { id: 'boxStickers', label: 'Box Stickers', Icon: Tags },
        { id: 'consolidateHbl', label: 'D2D → UPB/Gift', Icon: Layers },
        { id: 'cbmCalculator', label: 'CBM Calculator', Icon: Calculator },
      ],
    },
    {
      title: 'Documents',
      items: [
        { id: 'docInvoices', label: 'Invoices', Icon: CreditCard },
        { id: 'docReceipts', label: 'Receipts', Icon: ReceiptIcon },
        { id: 'docHbl', label: 'HBL', Icon: FileText },
      ],
    },
    {
      title: 'Shipping & tracking',
      items: [
        { id: 'shipments', label: 'Shipments', Icon: Ship },
        { id: 'packageScan', label: 'Package scan', Icon: ScanLine },
        { id: 'shippingAgents', label: 'Shipping agents', Icon: Building2 },
      ],
    },
    {
      title: 'People & inventory',
      items: [
        { id: 'customers', label: 'Shippers / Consignees', Icon: UserCircle2 },
        { id: 'boxItems', label: 'Items', Icon: ListOrdered },
        { id: 'emptyBoxes', label: 'Empty Boxes', Icon: Box },
      ],
    },
    {
      title: 'Finance',
      items: [
        { id: 'finance', label: 'Income & expenses', Icon: Wallet },
      ],
    },
    {
      title: 'Admin',
      items: [
        { id: 'employees', label: 'Users', Icon: Users },
        { id: 'reviews', label: 'Reviews', Icon: Star },
        { id: 'enquiries', label: 'Enquiries', Icon: MessageSquare },
        { id: 'reports', label: 'Reports', Icon: BarChart3 },
        { id: 'settings', label: 'Settings', Icon: Settings },
      ],
    },
      ],
      authUser?.role,
      authUser?.staffDepartment,
    ),
    [authUser?.role, authUser?.staffDepartment],
  );

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'in transit':
        return 'text-blue-600 bg-blue-100';
      case 'picked-up':
        return 'text-blue-600 bg-blue-100';
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      case 'exception':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-4 h-4" />;
      case 'in transit':
      case 'picked-up':
        return <Truck className="w-4 h-4" />;
      case 'processing':
        return <Clock className="w-4 h-4" />;
      case 'exception':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const searchableTabs = new Set([
    'dashboard',
    'collections',
    'boxStickers',
    'docInvoices',
    'docReceipts',
    'docHbl',
    'consolidateHbl',
    'emptyBoxes',
    'boxItems',
    'employees',
    'customers',
  ]);
  const topSearchEnabled = searchableTabs.has(activeTab);

  const topSearchValue =
    activeTab === 'dashboard' ||
    activeTab === 'collections' ||
    activeTab === 'boxStickers' ||
    activeTab === 'docInvoices' ||
    activeTab === 'docReceipts' ||
    activeTab === 'docHbl' ||
    activeTab === 'consolidateHbl'
      ? collectionsFilter.query
      : activeTab === 'emptyBoxes'
          ? emptyBoxesFilter.query
          : activeTab === 'boxItems'
            ? boxItemsFilter.query
            : activeTab === 'employees'
              ? usersFilter.query
              : activeTab === 'customers'
                ? customersFilter.query
                : '';

  const onTopSearchChange = (e) => {
    const v = e.target.value;
    if (
      activeTab === 'dashboard' ||
      activeTab === 'collections' ||
      activeTab === 'boxStickers' ||
      activeTab === 'docInvoices' ||
      activeTab === 'docReceipts' ||
      activeTab === 'docHbl' ||
      activeTab === 'consolidateHbl'
    ) {
      setCollectionsFilter((f) => ({ ...f, query: v }));
    } else if (activeTab === 'emptyBoxes') {
      setEmptyBoxesFilter((f) => ({ ...f, query: v }));
    } else if (activeTab === 'boxItems') {
      setBoxItemsFilter((f) => ({ ...f, query: v }));
    } else if (activeTab === 'employees') {
      setUsersFilter((f) => ({ ...f, query: v }));
    } else if (activeTab === 'customers') {
      setCustomersFilter((f) => ({ ...f, query: v }));
    }
  };

  const topSearchPlaceholder = (() => {
    switch (activeTab) {
      case 'dashboard':
      case 'collections':
      case 'docInvoices':
      case 'docReceipts':
      case 'docHbl':
      case 'consolidateHbl':
        return 'Search collections (no., shipper, passport…)…';
      case 'emptyBoxes':
        return 'Search empty boxes…';
      case 'boxItems':
        return 'Search items…';
      case 'employees':
        return 'Search users (name, email)…';
      case 'customers':
        return 'Search shippers / consignees…';
      default:
        return 'Search unavailable on this page';
    }
  })();

  const collectionToolbarFilters = (
    <div className="jet-admin-filter-row jet-admin-filter-row--tight">
      <div className="jet-admin-filter-search">
        <Search
          className="w-4 h-4"
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0.5,
            pointerEvents: 'none',
          }}
        />
        <input
          type="search"
          className="form-input"
          value={collectionsFilter.query}
          onChange={(e) => setCollectionsFilter((f) => ({ ...f, query: e.target.value }))}
          placeholder="Collection no., shipper, passport…"
          style={{ paddingLeft: 34 }}
          aria-label="Search collections"
        />
      </div>
      <select
        className="form-input"
        style={{ width: 'auto', minWidth: 130 }}
        value={collectionsFilter.status}
        onChange={(e) => setCollectionsFilter((f) => ({ ...f, status: e.target.value }))}
        aria-label="Collection status"
      >
        <option value="all">All statuses</option>
        <option value="pending">Pending</option>
        <option value="completed">Completed</option>
      </select>
      <select
        className="form-input"
        style={{ width: 'auto', minWidth: 150 }}
        value={collectionsFilter.clearing}
        onChange={(e) => setCollectionsFilter((f) => ({ ...f, clearing: e.target.value }))}
        aria-label="Clearing type"
      >
        <option value="all">All clearing types</option>
        <option value="gift">Gift</option>
        <option value="door_to_door">Door to Door</option>
        <option value="upb">UPB</option>
      </select>
      <button
        type="button"
        className="jet-btn jet-btn-secondary"
        onClick={() => setCollectionsFilter({ query: '', status: 'all', clearing: 'all' })}
      >
        Clear filters
      </button>
    </div>
  );

  const currentCollectionBoxes = getCurrentCollectionBoxes();
  const collectionSummary = getCollectionSummary(currentCollectionBoxes);

  return (
    <div className={`admin-layout jet-cargo-admin${sidebarOpen ? ' jet-sidebar-open' : ''}`}>
      {authUser?.requiresMfaSetup && authUser?.token && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mfa-setup-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            background: 'rgba(15, 23, 42, 0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '420px',
              width: '100%',
              padding: '1.75rem',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
            }}
          >
            <h2 id="mfa-setup-title" style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
              Set up authenticator
            </h2>
            <p style={{ margin: '0 0 1rem', color: '#64748b', fontSize: '0.9rem' }}>
              Scan this QR code in Google Authenticator, Authy, or Microsoft Authenticator. Then enter the 6-digit code to finish.
            </p>
            {mfaSetup.busy && !mfaSetup.qr && !mfaSetup.err && (
              <p style={{ color: '#64748b' }}>Loading QR code…</p>
            )}
            {mfaSetup.err && (
              <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{mfaSetup.err}</p>
            )}
            {mfaSetup.qr && (
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <img src={mfaSetup.qr} alt="Authenticator QR" style={{ maxWidth: '220px', height: 'auto' }} />
              </div>
            )}
            {mfaSetup.manualKey && (
              <p style={{ fontSize: '0.8rem', color: '#64748b', wordBreak: 'break-all', marginBottom: '1rem' }}>
                Manual key: <strong style={{ color: '#0f172a' }}>{mfaSetup.manualKey}</strong>
              </p>
            )}
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.875rem' }}>
              Verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaSetup.code}
              onChange={(e) => setMfaSetup((s) => ({ ...s, code: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
              className="form-input"
              placeholder="000000"
              style={{ letterSpacing: '0.2em', marginBottom: '1rem' }}
            />
            <button
              type="button"
              className="btn"
              style={{ width: '100%' }}
              disabled={mfaSetup.busy}
              onClick={submitMfaSetup}
            >
              {mfaSetup.busy ? 'Checking…' : 'Enable authenticator'}
            </button>
            {mfaSetup.info && (
              <p style={{ color: '#166534', marginTop: '0.75rem', fontSize: '0.875rem' }}>{mfaSetup.info}</p>
            )}
          </div>
        </div>
      )}
      <div className="jet-sidebar-overlay" aria-hidden="true" onClick={() => setSidebarOpen(false)} />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <aside className="admin-sidebar jet-sidebar">
        <div className="admin-brand jet-sidebar-brand">
          <img
            src={COMPANY_LOGO_SRC}
            alt="Jet Shipping Cargo"
            className="jet-sidebar-brand__logo"
            decoding="async"
          />
          <span>JET SHIPPING CARGO</span>
        </div>
        <nav className="admin-nav jet-sidebar-nav">
          {mainNavSections.map((section) => (
            <div key={section.title} className="jet-nav-section">
              <div className="jet-nav-section-title">{section.title}</div>
              {section.items.map((tab) => {
                const Icon = tab.Icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                      setSidebarOpen(false);
                      setActiveTab(tab.id);
                    }}
                    className={`admin-nav-item jet-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  >
                    <span className="icon"><Icon className="w-4 h-4" /></span>
                    <span className="label">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="jet-sidebar-footer">
          <Link
            to="/"
            className="jet-view-site"
            onClick={() => setSidebarOpen(false)}
          >
            <ExternalLink className="w-4 h-4" aria-hidden />
            View Site
          </Link>
          <div className="jet-sidebar-user">
            <div className="jet-sidebar-user-avatar">A</div>
            <div className="jet-sidebar-user-info">
              <strong>Admin</strong>
              <span>Super Administrator</span>
            </div>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="jet-sidebar-user-more"
                aria-label="Account menu"
                onClick={() => setSidebarUserMenuOpen((o) => !o)}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {sidebarUserMenuOpen && (
                <div className="jet-user-dropdown" style={{ right: 'auto', left: 0, top: '100%', marginTop: 4 }}>
                  <button type="button" onClick={() => { setSidebarUserMenuOpen(false); performLogout(); }}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <div className="admin-main jet-main">
        <header className="admin-header jet-topbar">
          <button
            type="button"
            className="jet-burger"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="jet-topbar-search">
            <Search className="w-4 h-4" />
            <input
              ref={topSearchRef}
              type="search"
              placeholder={topSearchPlaceholder}
              value={topSearchValue}
              onChange={onTopSearchChange}
              readOnly={!topSearchEnabled}
              aria-disabled={!topSearchEnabled}
              style={!topSearchEnabled ? { opacity: 0.65, cursor: 'not-allowed' } : undefined}
            />
            <kbd>Ctrl + /</kbd>
          </div>
          <div className="jet-topbar-actions">
            <ThemeToggle variant="admin" />
            <AdminNotificationPanel
              apiBase={API_BASE}
              authToken={authUser?.token}
              onNavigate={setActiveTab}
            />
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className="jet-topbar-user"
                onClick={() => setTopUserMenuOpen((o) => !o)}
              >
                <span className="jet-topbar-user-avatar">A</span>
                <span className="jet-topbar-user-label">Admin</span>
                <ChevronDown className="w-4 h-4" style={{ opacity: 0.6 }} />
              </button>
              {topUserMenuOpen && (
                <div className="jet-user-dropdown">
                  <button type="button" onClick={() => { setTopUserMenuOpen(false); performLogout(); }}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="admin-content jet-content">
          <div className="jet-page-head">
            <h1>{(pageMeta[activeTab] || pageMeta.dashboard).title}</h1>
            <p>{(pageMeta[activeTab] || pageMeta.dashboard).subtitle}</p>
          </div>

          {activeTab === 'dashboard' && (
            <>
              <AdminDashboard
                apiBase={API_BASE}
                authToken={authUser?.token}
                userRole={authUser?.role}
                onNavigate={setActiveTab}
                collections={collections}
                aggregateStats={aggregateStats}
                donutStyle={donutStyle}
                filteredRegularCollections={filteredRegularCollections}
                formatAdminDate={formatAdminDate}
                formatCurrencyRM={formatCurrencyRM}
                collectionRowTotalPrice={collectionRowTotalPrice}
                collectionStatusLabel={collectionStatusLabel}
              />

              <div className="jet-card" style={{ marginBottom: '1.25rem' }}>
                <div className="jet-table-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h2 className="jet-table-title" style={{ margin: 0 }}>
                      Recent Collections
                      {collections.length > 0 && (
                        <span style={{ fontWeight: 500, color: 'var(--jet-muted)', fontSize: '0.85rem', marginLeft: 8 }}>
                          ({filteredRegularCollections.length} of {regularCollectionsCount})
                        </span>
                      )}
                    </h2>
                    <button
                      type="button"
                      className="jet-btn jet-btn-primary"
                      onClick={() => setActiveTab('boxCollection')}
                    >
                      <Plus className="w-4 h-4" />
                      Add Collection
                    </button>
                  </div>
                  {collectionToolbarFilters}
                </div>
                {loadingCollections ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>Loading collections…</p>
                ) : (
                  <>
                    <div className="jet-table-wrap">
                      <table className="jet-table">
                        <thead>
                          <tr>
                            <th>Collection No.</th>
                            <th>Collection Date</th>
                            <th>Shipper</th>
                            <th>Clearing Type</th>
                            <th>Packages</th>
                            <th>CBM</th>
                            <th>Weight</th>
                            <th>Total Price</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCollections.map((collection) => {
                            const sum = getCollectionSummary(collection?.boxes || []);
                            const st = collectionStatusLabel(collection);
                            return (
                              <tr key={collection._id || collection.collectionNo}>
                                <td style={{ fontWeight: 600 }}>{collection.collectionNo || '—'}</td>
                                <td>{formatAdminDate(collection.collectionDate || collection.createdAt)}</td>
                                <td>{renderShipperCollectionCell(collection)}</td>
                                <td>{clearingTypeLabel(collection.clearingType)}</td>
                                <td>{sum.totalQty}</td>
                                <td>{sum.totalCbm.toFixed(2)}</td>
                                <td>{sum.totalWeight.toFixed(2)} kg</td>
                                <td>{formatCurrencyRM(collectionRowTotalPrice(collection))}</td>
                                <td>
                                  <span className={`jet-badge ${st === 'Completed' ? 'jet-badge-success' : 'jet-badge-warn'}`}>{st}</span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                                    <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" title="View" onClick={() => handleViewCollection(collection)}>
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" title="Edit" onClick={() => handleEditCollectionFromTable(collection)}>
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      className="jet-btn jet-btn-danger jet-btn-icon"
                                      title="Delete"
                                      onClick={() => handleDeleteCollection(collection._id || collection.id)}
                                      disabled={deletingCollection === (collection._id || collection.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {filteredRegularCollections.length === 0 && collections.length > 0 && (
                      <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '1rem' }}>No collections match the current filters.</p>
                    )}
                    <div className="jet-pagination">
                      <span>
                        Showing {filteredRegularCollections.length === 0 ? 0 : (collectionsPage - 1) * collectionsPageSize + 1} to {Math.min(collectionsPage * collectionsPageSize, filteredRegularCollections.length)} of {filteredRegularCollections.length} entries
                      </span>
                      <div className="jet-pagination-btns">
                        <button
                          type="button"
                          className="jet-page-btn"
                          disabled={collectionsPage <= 1}
                          onClick={() => setCollectionsPage((p) => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="w-4 h-4" style={{ margin: '0 auto' }} />
                        </button>
                        {paginationNums.map((num, idx) => (
                          <React.Fragment key={num}>
                            {idx > 0 && paginationNums[idx] - paginationNums[idx - 1] > 1 && (
                              <span style={{ padding: '0 0.25rem', color: 'var(--jet-muted)' }}>…</span>
                            )}
                            <button
                              type="button"
                              className={`jet-page-btn${collectionsPage === num ? ' active' : ''}`}
                              onClick={() => setCollectionsPage(num)}
                            >
                              {num}
                            </button>
                          </React.Fragment>
                        ))}
                        <button
                          type="button"
                          className="jet-page-btn"
                          disabled={collectionsPage >= collectionsTotalPages}
                          onClick={() => setCollectionsPage((p) => Math.min(collectionsTotalPages, p + 1))}
                          aria-label="Next page"
                        >
                          <ChevronRight className="w-4 h-4" style={{ margin: '0 auto' }} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {activeTab === 'cbmCalculator' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card">
                <CbmCalculator title="CBM Calculator" />
              </div>
            </motion.div>
          )}

          {/* Shipments Tab — sea freight workspace (API-backed) */}
          {activeTab === 'shipments' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <AdminShipmentsWorkspace
                apiBase={API_BASE}
                authToken={authUser?.token}
                collections={collections}
                loadingCollections={loadingCollections}
                shippersList={shippersList}
                consigneesList={consigneesList}
                isActive={activeTab === 'shipments'}
                getStatusColor={getStatusColor}
                getStatusIcon={getStatusIcon}
                onManageShippingAgents={() => setActiveTab('shippingAgents')}
              />
            </motion.div>
          )}

          {activeTab === 'shippingAgents' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <AdminShippingAgentsPanel
                apiBase={API_BASE}
                authToken={authUser?.token}
                isActive={activeTab === 'shippingAgents'}
              />
            </motion.div>
          )}

          {/* Empty Boxes Booking Tab */}
          {activeTab === 'emptyBoxes' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="jet-card">
                <div className="jet-table-toolbar" style={{ marginBottom: '1rem' }}>
                  <h2 className="jet-table-title" style={{ margin: 0 }}>
                    Empty Box Management
                  </h2>
                  <button
                    type="button"
                    className="jet-btn jet-btn-primary"
                    onClick={() => {
                      setEditingEmptyBoxId('');
                      resetEmptyBoxForm();
                      setShowEmptyBoxModal(true);
                    }}
                  >
                    <Plus size={16} />
                    Add New Empty Box
                  </button>
                </div>

                <div className="jet-admin-filter-row">
                  <div className="jet-admin-filter-search">
                    <Search
                      className="w-4 h-4"
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0.5,
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="search"
                      className="form-input"
                      value={emptyBoxesFilter.query}
                      onChange={(e) => setEmptyBoxesFilter((f) => ({ ...f, query: e.target.value }))}
                      placeholder="Search name, official name, description…"
                      style={{ paddingLeft: 34 }}
                      aria-label="Search empty boxes"
                    />
                  </div>
                  <select
                    className="form-input"
                    style={{ width: 'auto', minWidth: 130 }}
                    value={emptyBoxesFilter.status}
                    onChange={(e) => setEmptyBoxesFilter((f) => ({ ...f, status: e.target.value }))}
                    aria-label="Empty box status"
                  >
                    <option value="all">All statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <button
                    type="button"
                    className="jet-btn jet-btn-secondary"
                    onClick={() => setEmptyBoxesFilter({ query: '', status: 'all' })}
                  >
                    Clear filters
                  </button>
                </div>

                {loadingEmptyBoxes ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '2rem' }}>
                    Loading empty boxes...
                  </p>
                ) : emptyBoxes.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '2rem' }}>
                    No empty boxes added yet.
                  </p>
                ) : filteredEmptyBoxes.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '2rem' }}>
                    No empty boxes match the current filters.
                  </p>
                ) : (
                  <div className="jet-table-wrap">
                    <table className="jet-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'center' }}>Image</th>
                          <th>Box Name</th>
                          <th>Official Box Name</th>
                          <th style={{ textAlign: 'center' }}>Dimensions (inch)</th>
                          <th style={{ textAlign: 'center' }}>Weight (kg)</th>
                          <th style={{ textAlign: 'center' }}>UPB/Gift (RM)</th>
                          <th style={{ textAlign: 'center' }}>Door to Door (RM)</th>
                          <th style={{ textAlign: 'center' }}>Available Stock</th>
                          <th style={{ textAlign: 'center' }}>Status</th>
                          <th style={{ textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmptyBoxes.map((box) => (
                          <tr key={box._id}>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ width: '44px', height: '44px', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                                {box.boxImageUrl ? (
                                  <img src={getPreviewUrl(box.boxImageUrl, box.updatedAt || box._id || '')} alt="box" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <span style={{ color: '#9ca3af', fontSize: '10px' }}>N/A</span>
                                )}
                              </div>
                            </td>
                            <td>{box.boxName || '-'}</td>
                            <td>{box.secondName || '-'}</td>
                            <td style={{ textAlign: 'center' }}>
                              {box.lengthInch || '-'} x {box.widthInch || '-'} x {box.heightInch || '-'}
                            </td>
                            <td style={{ textAlign: 'center' }}>{box.weightKg || '-'}</td>
                            <td style={{ textAlign: 'center' }}>{formatOptionalRM(box.priceUpbGift)}</td>
                            <td style={{ textAlign: 'center' }}>{formatOptionalRM(box.priceDoorToDoor)}</td>
                            <td style={{ textAlign: 'center' }}>{box.quantity}</td>
                            <td style={{ textAlign: 'center' }}>{box.status || 'Active'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={() => openEmptyBoxEditor(box)}>
                                  <Edit size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="jet-btn jet-btn-danger jet-btn-icon"
                                  onClick={() => handleDeleteEmptyBox(box._id)}
                                  disabled={deletingEmptyBoxId === box._id}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {showEmptyBoxModal && (
                <div className="jet-admin-modal-overlay jet-admin-modal-overlay--dark">
                  <div className="jet-admin-modal-panel" style={{ maxWidth: '720px' }}>
                    <div className="jet-admin-modal-panel__body">
                    <div className="jet-admin-modal-header">
                      <h3 style={{ margin: 0 }}>{editingEmptyBoxId ? 'Update Empty Box' : 'Add New Empty Box'}</h3>
                      <button
                        type="button"
                        className="jet-btn jet-btn-secondary jet-btn-icon"
                        onClick={closeEmptyBoxModal}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    <div className="jet-admin-form-stack">
                      <label className="form-label">Box Name:</label>
                      <input className="form-input" value={emptyBoxForm.boxName} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, boxName: e.target.value }))} />

                      <label className="form-label">Official Box Name:</label>
                      <input className="form-input" value={emptyBoxForm.secondName} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, secondName: e.target.value }))} />

                      <label className="form-label">Length (inch):</label>
                      <input className="form-input" value={emptyBoxForm.lengthInch} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, lengthInch: e.target.value }))} />

                      <label className="form-label">Width (inch):</label>
                      <input className="form-input" value={emptyBoxForm.widthInch} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, widthInch: e.target.value }))} />

                      <label className="form-label">Height (inch):</label>
                      <input className="form-input" value={emptyBoxForm.heightInch} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, heightInch: e.target.value }))} />

                      <label className="form-label">Weight (kg):</label>
                      <input className="form-input" value={emptyBoxForm.weightKg} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, weightKg: e.target.value }))} />

                      <label className="form-label">Price RM (UPB / Gift):</label>
                      <input className="form-input" value={emptyBoxForm.priceUpbGift} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, priceUpbGift: e.target.value }))} />

                      <label className="form-label">Price RM (Door to Door):</label>
                      <input className="form-input" value={emptyBoxForm.priceDoorToDoor} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, priceDoorToDoor: e.target.value }))} />

                      <label className="form-label">Available Stock:</label>
                      <input type="number" min="0" className="form-input" value={emptyBoxForm.quantity} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, quantity: Number.parseInt(e.target.value || '0', 10) }))} />

                      <label className="form-label">Description:</label>
                      <textarea className="form-textarea" rows="3" value={emptyBoxForm.description} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, description: e.target.value }))} />

                      <label className="form-label">Box Image:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="jet-box-image-preview">
                          {(emptyBoxForm.boxImage || emptyBoxForm.boxImageUrl) ? (
                            <img src={getPreviewUrl(emptyBoxForm.boxImage || emptyBoxForm.boxImageUrl, editingEmptyBoxId || '')} alt="empty-box" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span>No Image</span>
                          )}
                        </div>
                        <label className="jet-btn jet-btn-secondary" style={{ cursor: 'pointer' }}>
                          Choose File
                          <input type="file" accept="image/*" onChange={(e) => handleEmptyBoxImage(e.target.files?.[0])} style={{ display: 'none' }} />
                        </label>
                      </div>

                      <label className="form-label">Status:</label>
                      <select className="form-input" value={emptyBoxForm.status} onChange={(e) => setEmptyBoxForm((p) => ({ ...p, status: e.target.value }))}>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>

                      <button type="button" className="jet-btn jet-btn-success" onClick={handleAddEmptyBox} disabled={savingEmptyBox} style={{ marginTop: '0.5rem' }}>
                        {savingEmptyBox ? 'Saving...' : (editingEmptyBoxId ? 'Update Empty Box' : 'Add Empty Box')}
                      </button>
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'boxItems' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card">
                <p style={{ color: 'var(--jet-muted)', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Item names used in Collection Box Type.
                </p>
                <div style={{ marginBottom: '1rem', maxWidth: 'min(420px, 100%)' }}>
                  <label className="form-label">Filter items</label>
                  <div style={{ position: 'relative' }}>
                    <Search
                      className="w-4 h-4"
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0.5,
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="search"
                      className="form-input"
                      value={boxItemsFilter.query}
                      onChange={(e) => setBoxItemsFilter((f) => ({ ...f, query: e.target.value }))}
                      placeholder="Search item or official name…"
                      style={{ paddingLeft: 34 }}
                      aria-label="Filter box items"
                    />
                  </div>
                </div>
                <div className="grid grid-3" style={{ gap: '0.75rem', alignItems: 'end', marginBottom: '1rem' }}>
                  <div>
                    <label className="form-label">Item Name</label>
                    <input
                      className="form-input"
                      value={boxItemForm.itemName}
                      onChange={(e) => setBoxItemForm((p) => ({ ...p, itemName: e.target.value }))}
                      placeholder="e.g. JET G BOX"
                    />
                  </div>
                  <div>
                    <label className="form-label">Official Name</label>
                    <input
                      className="form-input"
                      value={boxItemForm.officialName}
                      onChange={(e) => setBoxItemForm((p) => ({ ...p, officialName: e.target.value }))}
                      placeholder="e.g. Jet Gift Box"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="jet-btn jet-btn-primary" type="button" onClick={handleSaveBoxItem} disabled={savingBoxItem}>
                      {savingBoxItem ? 'Saving...' : (editingBoxItemId ? 'Update Item' : 'Add Item')}
                    </button>
                    {editingBoxItemId && (
                      <button className="jet-btn jet-btn-secondary" type="button" onClick={resetBoxItemForm}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                {loadingBoxItems ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '1rem' }}>Loading items...</p>
                ) : boxItems.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '1rem' }}>No items added yet.</p>
                ) : filteredBoxItems.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)', textAlign: 'center', padding: '1rem' }}>No items match the current search.</p>
                ) : (
                  <div className="jet-table-wrap">
                    <table className="jet-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Official Name</th>
                          <th style={{ textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBoxItems.map((item) => (
                          <tr key={item._id}>
                            <td>{item.itemName}</td>
                            <td>{item.officialName}</td>
                            <td style={{ textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={() => openBoxItemEditor(item)}>
                                  <Edit size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="jet-btn jet-btn-danger jet-btn-icon"
                                  onClick={() => handleDeleteBoxItem(item._id)}
                                  disabled={deletingBoxItemId === item._id}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Box Collection Tab */}
          {activeTab === 'boxCollection' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="jet-card">
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '1rem' }}>
                  Box Collection
                </h2>

                {/* Collection Meta */}
                <div className="grid grid-3" style={{ gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="form-label">Collection No. (unique)</label>
                    <input
                      className="form-input"
                      value={boxCollectionForm.collectionNo}
                      onChange={(e)=>updateBoxForm('collectionNo', e.target.value)}
                      placeholder="Enter unique collection number"
                    />
                  </div>
                  <div>
                    <label className="form-label">Collection Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={boxCollectionForm.collectionDate || ''}
                      onChange={(e)=>updateBoxForm('collectionDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label">Clearing Type</label>
                    <select className="form-input" value={boxCollectionForm.clearingType} onChange={(e)=>updateBoxForm('clearingType', e.target.value)}>
                      <option value="">Select Clearing Type</option>
                      <option value="gift">Gift</option>
                      <option value="door_to_door">Door to Door</option>
                      <option value="upb">UPB</option>
                    </select>
                  </div>
                </div>

                {/* Shipper Details */}
                <h3 style={{ fontWeight: 600, margin: '1rem 0' }}>Shipper Details</h3>
                <div className="grid grid-2" style={{ gap: '1rem' }}>
                  <div>
                    <label className="form-label">Name with Initials</label>
                    <input className="form-input" value={boxCollectionForm.shipper.nameWithInitials} onChange={(e)=>updateBoxForm('shipper.nameWithInitials', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Passport No</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                      <input 
                        className="form-input" 
                        value={boxCollectionForm.shipper.passportNo} 
                        onChange={(e)=>updateBoxForm('shipper.passportNo', e.target.value)}
                        style={{ flex: '1 1 160px', minWidth: 0 }}
                        placeholder="Enter passport number"
                      />
                      <button 
                        type="button"
                        className="btn btn-secondary" 
                        onClick={() => searchShipper(boxCollectionForm.shipper.passportNo)}
                        disabled={searchingShipper || !boxCollectionForm.shipper.passportNo.trim()}
                        style={{ padding: '0.5rem', minWidth: '80px' }}
                      >
                        {searchingShipper ? '...' : 'Search'}
                      </button>
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Address</label>
                    <textarea className="form-textarea" rows="2" value={boxCollectionForm.shipper.address} onChange={(e)=>updateBoxForm('shipper.address', e.target.value)} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Mobile Numbers</label>
                    {boxCollectionForm.shipper.mobiles.map((m,i)=>(
                      <input key={i} className="form-input" style={{ marginBottom: '0.5rem' }} value={m} onChange={(e)=>updateMobile('shipper', i, e.target.value)} />
                    ))}
                    <button className="btn btn-secondary" type="button" onClick={()=>addMobile('shipper')} style={{ marginTop: '0.25rem' }}>+ Add Mobile</button>
                  </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Passport Photo</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                          {boxCollectionForm.shipper.passportPhoto ? (
                            <img src={getPreviewUrl(boxCollectionForm.shipper.passportPhoto)} alt="passport" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
                          )}
                        </div>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                          Choose File
                          <input type="file" accept="image/*" onChange={(e)=>handleFile('shipper.passportPhoto', e.target.files?.[0])} style={{ display: 'none' }} />
                        </label>
                      </div>
                    </div>
                </div>

                {/* Consignee Details */}
                <h3 style={{ fontWeight: 600, margin: '1rem 0' }}>Consignee Details</h3>
                <div className="grid grid-2" style={{ gap: '1rem' }}>
                  <div>
                    <label className="form-label">Name with Initials</label>
                    <input className="form-input" value={boxCollectionForm.consignee.nameWithInitials} onChange={(e)=>updateBoxForm('consignee.nameWithInitials', e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">ID Type</label>
                    <select className="form-input" value={boxCollectionForm.consignee.idType} onChange={(e)=>updateBoxForm('consignee.idType', e.target.value)}>
                      <option value="">Select ID Type</option>
                      <option value="passport">Passport</option>
                      <option value="nic">NIC</option>
                      <option value="driving_licence">Driving Licence</option>
                    </select>
                  </div>
                  {boxCollectionForm.consignee.idType === 'passport' ? (
                    <div>
                      <label className="form-label">Passport No</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                        <input 
                          className="form-input" 
                          value={boxCollectionForm.consignee.passportNo} 
                          onChange={(e)=>updateBoxForm('consignee.passportNo', e.target.value)}
                          style={{ flex: '1 1 160px', minWidth: 0 }}
                          placeholder="Enter passport number"
                        />
                        <button 
                          type="button"
                          className="btn btn-secondary" 
                          onClick={() => searchConsignee(boxCollectionForm.consignee.passportNo)}
                          disabled={searchingConsignee || !boxCollectionForm.consignee.passportNo.trim()}
                          style={{ padding: '0.5rem', minWidth: '80px' }}
                        >
                          {searchingConsignee ? '...' : 'Search'}
                        </button>
                      </div>
                    </div>
                  ) : boxCollectionForm.consignee.idType === 'nic' ? (
                    <div>
                      <label className="form-label">NIC No</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                        <input 
                          className="form-input" 
                          value={boxCollectionForm.consignee.nicNo} 
                          onChange={(e)=>updateBoxForm('consignee.nicNo', e.target.value)}
                          style={{ flex: '1 1 160px', minWidth: 0 }}
                          placeholder="Enter NIC number"
                        />
                        <button 
                          type="button"
                          className="btn btn-secondary" 
                          onClick={() => searchConsignee(boxCollectionForm.consignee.nicNo)}
                          disabled={searchingConsignee || !boxCollectionForm.consignee.nicNo.trim()}
                          style={{ padding: '0.5rem', minWidth: '80px' }}
                        >
                          {searchingConsignee ? '...' : 'Search'}
                        </button>
                      </div>
                    </div>
                  ) : boxCollectionForm.consignee.idType === 'driving_licence' ? (
                    <div>
                      <label className="form-label">Driving Licence No</label>
                      <input
                        className="form-input"
                        value={boxCollectionForm.consignee.drivingLicenceNo}
                        onChange={(e)=>updateBoxForm('consignee.drivingLicenceNo', e.target.value)}
                        placeholder="Enter driving licence number"
                      />
                    </div>
                  ) : null}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Address</label>
                    <textarea className="form-textarea" rows="2" value={boxCollectionForm.consignee.address} onChange={(e)=>updateBoxForm('consignee.address', e.target.value)} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Mobile Numbers</label>
                    {boxCollectionForm.consignee.mobiles.map((m,i)=>(
                      <input key={i} className="form-input" style={{ marginBottom: '0.5rem' }} value={m} onChange={(e)=>updateMobile('consignee', i, e.target.value)} />
                    ))}
                    <button className="btn btn-secondary" type="button" onClick={()=>addMobile('consignee')} style={{ marginTop: '0.25rem' }}>+ Add Mobile</button>
                  </div>
                  {boxCollectionForm.consignee.idType === 'passport' ? (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Passport Photo</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                          {boxCollectionForm.consignee.passportPhoto ? (
                            <img src={getPreviewUrl(boxCollectionForm.consignee.passportPhoto)} alt="passport" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
                          )}
                        </div>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                          Choose File
                          <input type="file" accept="image/*" onChange={(e)=>handleFile('consignee.passportPhoto', e.target.files?.[0])} style={{ display: 'none' }} />
                        </label>
                      </div>
                    </div>
                  ) : boxCollectionForm.consignee.idType === 'nic' ? (
                    <div className="grid grid-2" style={{ gap: '1rem', gridColumn: '1 / -1' }}>
                        <div>
                          <label className="form-label">NIC Front</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                              {boxCollectionForm.consignee.nicFront ? (
                                <img src={getPreviewUrl(boxCollectionForm.consignee.nicFront)} alt="nic-front" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
                              )}
                            </div>
                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                              Choose File
                              <input type="file" accept="image/*" onChange={(e)=>handleFile('consignee.nicFront', e.target.files?.[0])} style={{ display: 'none' }} />
                            </label>
                          </div>
                        </div>
                      <div>
                        <label className="form-label">NIC Back</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                              {boxCollectionForm.consignee.nicBack ? (
                                <img src={getPreviewUrl(boxCollectionForm.consignee.nicBack)} alt="nic-back" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
                              )}
                            </div>
                            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                              Choose File
                              <input type="file" accept="image/*" onChange={(e)=>handleFile('consignee.nicBack', e.target.files?.[0])} style={{ display: 'none' }} />
                            </label>
                          </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="form-label">Driving Licence Photo</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                          {boxCollectionForm.consignee.drivingLicencePhoto ? (
                            <img src={getPreviewUrl(boxCollectionForm.consignee.drivingLicencePhoto)} alt="driving-licence" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
                          )}
                        </div>
                        <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                          Choose File
                          <input type="file" accept="image/*" onChange={(e)=>handleFile('consignee.drivingLicencePhoto', e.target.files?.[0])} style={{ display: 'none' }} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Boxes Table */}
                <h3 style={{ fontWeight: 600, margin: '1rem 0' }}>Boxes</h3>
                <div style={{ marginBottom: '0.75rem', maxWidth: '240px' }}>
                  <label className="form-label">Dimension Type</label>
                  <select
                    className="form-input"
                    value={boxCollectionForm.boxDimensionUnit || 'cm'}
                    onChange={(e) => handleChangeBoxDimensionUnit(e.target.value)}
                  >
                    <option value="cm">CM</option>
                    <option value="inch">Inch</option>
                  </select>
                </div>
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Item Type</th>
                        <th>Official Item Name</th>
                        <th>Description</th>
                        <th>D2D</th>
                        <th>Quantity</th>
                        <th>L</th>
                        <th>W</th>
                        <th>H</th>
                        <th>Unit</th>
                        <th>Unit CBM</th>
                        <th>Total CBM</th>
                        <th>Unit Weight (kg)</th>
                        <th>Weight (Auto)</th>
                        <th>Unit Price (RM)</th>
                        <th>Price (Auto)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {boxCollectionForm.boxes.map((b, i) => (
                        <tr key={i}>
                          <td>
                            <select className="form-input" value={b.boxType} onChange={(e)=>updateBoxRow(i,'boxType',e.target.value)}>
                              <option value="">Select</option>
                              {getBoxTypeOptions().map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="form-input"
                              value={b.officialBoxName || ''}
                              onChange={(e)=>updateBoxRow(i,'officialBoxName',e.target.value)}
                              placeholder="Official box name"
                            />
                          </td>
                          <td><input className="form-input" value={b.description} onChange={(e)=>updateBoxRow(i,'description',e.target.value)} /></td>
                          <td>
                            <input
                              className="form-input"
                              style={{ minWidth: '72px' }}
                              value={b.d2d || ''}
                              onChange={(e) => updateBoxRow(i, 'd2d', e.target.value)}
                              placeholder="—"
                            />
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: '140px' }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ padding: '0.25rem 0.45rem', minWidth: '30px' }}
                                onClick={() => {
                                  const current = Number.parseInt(b.quantity, 10) || 1;
                                  updateBoxRow(i, 'quantity', Math.max(1, current - 1));
                                }}
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                className="form-input"
                                style={{ width: '72px', textAlign: 'center' }}
                                value={b.quantity}
                                onChange={(e)=>updateBoxRow(i,'quantity',e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ padding: '0.25rem 0.45rem', minWidth: '30px' }}
                                onClick={() => {
                                  const current = Number.parseInt(b.quantity, 10) || 1;
                                  updateBoxRow(i, 'quantity', current + 1);
                                }}
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td><input type="number" min="0" step="any" className="form-input" style={{ minWidth: '86px' }} value={b.length} onChange={(e)=>updateBoxRow(i,'length',e.target.value)} /></td>
                          <td><input type="number" min="0" step="any" className="form-input" style={{ minWidth: '86px' }} value={b.width} onChange={(e)=>updateBoxRow(i,'width',e.target.value)} /></td>
                          <td><input type="number" min="0" step="any" className="form-input" style={{ minWidth: '86px' }} value={b.height} onChange={(e)=>updateBoxRow(i,'height',e.target.value)} /></td>
                          <td style={{ minWidth: '70px' }}>{(b.dimensionsUnit || boxCollectionForm.boxDimensionUnit || 'cm').toUpperCase()}</td>
                          <td><input className="form-input" value={b.cbm} readOnly /></td>
                          <td><input className="form-input" value={b.totalCbm || ''} readOnly /></td>
                          <td><input className="form-input" value={b.weight} onChange={(e)=>updateBoxRow(i,'weight',e.target.value)} /></td>
                          <td><input className="form-input" value={b.totalWeight || ''} readOnly /></td>
                          <td><input className="form-input" value={b.price} onChange={(e)=>updateBoxRow(i,'price',e.target.value)} /></td>
                          <td><input className="form-input" value={b.totalPrice || ''} readOnly /></td>
                          <td>
                            <button className="btn btn-secondary" type="button" onClick={()=>removeBoxRow(i)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <button className="btn" type="button" onClick={addBoxRow}>+ Add Box</button>
                </div>

                {/* Charges + Summary */}
                <div style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  {(() => {
                    const pricing = getCollectionPricingSummary();
                    return (
                      <>
                        <div className="grid grid-2" style={{ gap: '1rem' }}>
                          <div className="admin-card" style={{ padding: '12px' }}>
                            <h4 style={{ margin: '0 0 0.75rem 0' }}>Charges</h4>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Sub Total:</strong> RM {pricing.subTotal.toFixed(2)}
                            </div>
                            <div className="grid grid-2" style={{ gap: '0.6rem' }}>
                              <div>
                                <label className="form-label">Transport Charges (RM)</label>
                                <input
                                  className="form-input"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={boxCollectionForm.charges?.transportCharge || ''}
                                  onChange={(e) => updateBoxForm('charges.transportCharge', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label">Packing Charges (RM)</label>
                                <input
                                  className="form-input"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={boxCollectionForm.charges?.packingCharge || ''}
                                  onChange={(e) => updateBoxForm('charges.packingCharge', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label">Other Charges (RM)</label>
                                <input
                                  className="form-input"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={boxCollectionForm.charges?.otherCharge || ''}
                                  onChange={(e) => updateBoxForm('charges.otherCharge', e.target.value)}
                                />
                              </div>
                              <div>
                                <label className="form-label">Discount Type</label>
                                <select
                                  className="form-input"
                                  value={boxCollectionForm.charges?.discountType || 'percentage'}
                                  onChange={(e) => updateBoxForm('charges.discountType', e.target.value)}
                                >
                                  <option value="percentage">Percentage (%)</option>
                                  <option value="amount">Amount (RM)</option>
                                </select>
                              </div>
                              <div>
                                <label className="form-label">
                                  Discount ({(boxCollectionForm.charges?.discountType || 'percentage') === 'percentage' ? '%' : 'RM'})
                                </label>
                                <input
                                  className="form-input"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={boxCollectionForm.charges?.discountValue || ''}
                                  onChange={(e) => updateBoxForm('charges.discountValue', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="admin-card" style={{ padding: '12px' }}>
                            <h4 style={{ margin: '0 0 0.75rem 0' }}>Summary</h4>
                            <div className="jet-admin-kv-grid">
                              <div>Total Packages</div><div>{pricing.totalQty}</div>
                              <div>Total CBM</div><div>{pricing.totalCbm.toFixed(2)}</div>
                              <div>Total Weight</div><div>{pricing.totalWeight.toFixed(2)} kg</div>
                              <div>Sub Total</div><div>RM {pricing.subTotal.toFixed(2)}</div>
                              <div>Transport Charges</div><div>RM {pricing.transportCharge.toFixed(2)}</div>
                              <div>Packing Charges</div><div>RM {pricing.packingCharge.toFixed(2)}</div>
                              <div>Other Charges</div><div>RM {pricing.otherCharge.toFixed(2)}</div>
                              <div>Discount</div><div>- RM {pricing.discountAmount.toFixed(2)}</div>
                              <div style={{ fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: '0.45rem' }}>Total Price</div>
                              <div style={{ fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: '0.45rem' }}>RM {pricing.totalPrice.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div
                  style={{
                    marginTop: '1.25rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <button className="btn btn-secondary" type="button" onClick={() => setShowBoxPreview(true)}>
                    Preview Summary
                  </button>
                  <button className="btn" type="button" onClick={handleSubmitCollection} disabled={isSubmittingCollection}>
                    {isSubmittingCollection ? 'Submitting…' : 'Submit Collection'}
                  </button>
                </div>

                {/* Included Collections Part */}
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <h3 style={{ fontWeight: 600, margin: 0 }}>
                        Submitted Collections ({filteredRegularCollections.length}
                        {filteredRegularCollections.length !== regularCollectionsCount ? ` of ${regularCollectionsCount}` : ''})
                      </h3>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={fetchCollections}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                        >
                          <Search size={14} />
                          Refresh
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setActiveTab('collections')}
                        >
                          Open Collections Tab
                        </button>
                      </div>
                    </div>
                    {collectionToolbarFilters}
                  </div>

                  {loadingCollections ? (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      <p style={{ color: '#6b7280' }}>Loading collections...</p>
                    </div>
                  ) : collections.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                      No collections submitted yet.
                    </div>
                  ) : filteredRegularCollections.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                      No collections match the current filters.
                    </div>
                  ) : (
                    <div className="jet-table-wrap">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Collection No.</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Clearing Type</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Shipper</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Consignee</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Collection Date</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Boxes</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRegularCollections.map((collection, index) => (
                            <tr key={`${collection._id || collection.id || index}-box-tab`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.75rem', fontWeight: '500' }}>{collection.collectionNo}</td>
                              <td style={{ padding: '0.75rem' }}>{collection.clearingType}</td>
                              <td style={{ padding: '0.75rem' }}>
                                {collection.shipperPassportNo ? (
                                  <button
                                    type="button"
                                    className="jet-person-link"
                                    onClick={() => viewShipperDetails(collection.shipperPassportNo)}
                                  >
                                    {shipperNameForCollection(collection)}
                                  </button>
                                ) : '—'}
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                {collection.consigneeId ? (
                                  <button
                                    type="button"
                                    className="jet-person-link"
                                    onClick={() => viewConsigneeDetails(collection.consigneeId)}
                                  >
                                    {consigneeNameForCollection(collection)}
                                  </button>
                                ) : '—'}
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                {(collection.collectionDate || collection.createdAt)
                                  ? new Date(collection.collectionDate || collection.createdAt).toLocaleDateString()
                                  : '-'}
                              </td>
                              <td style={{ padding: '0.75rem' }}>{collection.boxes?.length || 0}</td>
                              <td style={{ padding: '0.75rem' }}>
                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                  <button
                                    className="btn btn-secondary"
                                    style={{ padding: '0.4rem', fontSize: '0.8rem' }}
                                    onClick={() => handleViewCollection(collection)}
                                    title="View"
                                  >
                                    <Eye size={13} />
                                  </button>
                                  <button
                                    className="btn"
                                    style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#10b981', color: 'white' }}
                                    onClick={() => handleCreateInvoice(collection)}
                                  >
                                    Invoice
                                  </button>
                                  <button
                                    className="btn"
                                    style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#3b82f6', color: 'white' }}
                                    onClick={() => handleCreateReceipt(collection)}
                                  >
                                    Receipt
                                  </button>
                                  <button
                                    className="btn"
                                    style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', backgroundColor: '#8b5cf6', color: 'white' }}
                                    onClick={() => handleCreateHBL(collection)}
                                  >
                                    HBL
                                  </button>
                                  {(() => {
                                    const sc = canPrintStickers(collection, collections);
                                    return (
                                      <button
                                        className="btn"
                                        style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', backgroundColor: sc.ok ? '#0d9488' : '#94a3b8', color: 'white' }}
                                        onClick={() => handleCreateStickers(collection)}
                                        disabled={!sc.ok}
                                        title={sc.reason || 'Print A6 stickers'}
                                      >
                                        Stickers
                                      </button>
                                    );
                                  })()}
                                  <button
                                    className="btn"
                                    style={{ padding: '0.4rem', fontSize: '0.8rem', backgroundColor: '#dc2626', color: 'white' }}
                                    onClick={() => handleDeleteCollection(collection._id || collection.id)}
                                    disabled={deletingCollection === (collection._id || collection.id)}
                                    title="Delete"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Collections Tab */}
          {activeTab === 'collections' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card">
                <div className="jet-table-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h2 className="jet-table-title" style={{ margin: 0 }}>
                      All Collections ({filteredRegularCollections.length}
                      {filteredRegularCollections.length !== regularCollectionsCount ? ` of ${regularCollectionsCount}` : ''})
                    </h2>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" className="jet-btn jet-btn-secondary" onClick={fetchCollections}>
                        <Search className="w-4 h-4" />
                        Refresh
                      </button>
                      <button type="button" className="jet-btn jet-btn-primary" onClick={() => setActiveTab('boxCollection')}>
                        <Plus className="w-4 h-4" />
                        Add Collection
                      </button>
                    </div>
                  </div>
                  {collectionToolbarFilters}
                </div>
                {loadingCollections ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>Loading collections…</p>
                ) : collections.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>No collections yet. Use Add Collection to create one.</p>
                ) : filteredRegularCollections.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>No collections match the current filters.</p>
                ) : (
                  <>
                    <div className="jet-table-wrap">
                      <table className="jet-table">
                        <thead>
                          <tr>
                            <th>Collection No.</th>
                            <th>Collection Date</th>
                            <th>Shipper</th>
                            <th>Clearing Type</th>
                            <th>Packages</th>
                            <th>CBM</th>
                            <th>Weight</th>
                            <th>Total Price</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedCollections.map((collection) => {
                            const sum = getCollectionSummary(collection?.boxes || []);
                            const st = collectionStatusLabel(collection);
                            return (
                              <tr key={collection._id || collection.collectionNo}>
                                <td style={{ fontWeight: 600 }}>{collection.collectionNo || '—'}</td>
                                <td>{formatAdminDate(collection.collectionDate || collection.createdAt)}</td>
                                <td>{renderShipperCollectionCell(collection)}</td>
                                <td>{clearingTypeLabel(collection.clearingType)}</td>
                                <td>{sum.totalQty}</td>
                                <td>{sum.totalCbm.toFixed(2)}</td>
                                <td>{sum.totalWeight.toFixed(2)} kg</td>
                                <td>{formatCurrencyRM(collectionRowTotalPrice(collection))}</td>
                                <td>
                                  <span className={`jet-badge ${st === 'Completed' ? 'jet-badge-success' : 'jet-badge-warn'}`}>{st}</span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', maxWidth: 280 }}>
                                    <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" title="View" onClick={() => handleViewCollection(collection)}>
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" title="Edit" onClick={() => handleEditCollectionFromTable(collection)}>
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button type="button" className="jet-btn jet-btn-secondary" style={{ fontSize: '0.72rem', padding: '0.35rem 0.5rem' }} onClick={() => handleCreateInvoice(collection)}>Invoice</button>
                                    <button type="button" className="jet-btn jet-btn-secondary" style={{ fontSize: '0.72rem', padding: '0.35rem 0.5rem' }} onClick={() => handleCreateReceipt(collection)}>Receipt</button>
                                    <button type="button" className="jet-btn jet-btn-secondary" style={{ fontSize: '0.72rem', padding: '0.35rem 0.5rem' }} onClick={() => handleCreateHBL(collection)}>HBL</button>
                                    {(() => {
                                      const sc = canPrintStickers(collection, collections);
                                      return (
                                        <button
                                          type="button"
                                          className="jet-btn jet-btn-secondary"
                                          style={{ fontSize: '0.72rem', padding: '0.35rem 0.5rem' }}
                                          onClick={() => handleCreateStickers(collection)}
                                          disabled={!sc.ok}
                                          title={sc.reason || 'Print A6 package stickers'}
                                        >
                                          Stickers
                                        </button>
                                      );
                                    })()}
                                    <button
                                      type="button"
                                      className="jet-btn jet-btn-danger jet-btn-icon"
                                      title="Delete"
                                      onClick={() => handleDeleteCollection(collection._id || collection.id)}
                                      disabled={deletingCollection === (collection._id || collection.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="jet-pagination">
                      <span>
                        Showing {filteredRegularCollections.length === 0 ? 0 : (collectionsPage - 1) * collectionsPageSize + 1} to {Math.min(collectionsPage * collectionsPageSize, filteredRegularCollections.length)} of {filteredRegularCollections.length} entries
                      </span>
                      <div className="jet-pagination-btns">
                        <button type="button" className="jet-page-btn" disabled={collectionsPage <= 1} onClick={() => setCollectionsPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                          <ChevronLeft className="w-4 h-4" style={{ margin: '0 auto' }} />
                        </button>
                        {paginationNums.map((num, idx) => (
                          <React.Fragment key={num}>
                            {idx > 0 && paginationNums[idx] - paginationNums[idx - 1] > 1 && (
                              <span style={{ padding: '0 0.25rem', color: 'var(--jet-muted)' }}>…</span>
                            )}
                            <button type="button" className={`jet-page-btn${collectionsPage === num ? ' active' : ''}`} onClick={() => setCollectionsPage(num)}>{num}</button>
                          </React.Fragment>
                        ))}
                        <button type="button" className="jet-page-btn" disabled={collectionsPage >= collectionsTotalPages} onClick={() => setCollectionsPage((p) => Math.min(collectionsTotalPages, p + 1))} aria-label="Next page">
                          <ChevronRight className="w-4 h-4" style={{ margin: '0 auto' }} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'boxStickers' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card">
                <div className="jet-table-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h2 className="jet-table-title" style={{ margin: 0 }}>
                      Box stickers ({stickerEligibleCollections.length})
                    </h2>
                    <button type="button" className="jet-btn jet-btn-secondary" onClick={fetchCollections}>
                      <Search className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--jet-muted)', lineHeight: 1.5 }}>
                    Print A6 QR/barcode stickers for packages. Only collections with registered boxes appear here.
                  </p>
                  {collectionToolbarFilters}
                </div>
                {loadingCollections ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>Loading collections…</p>
                ) : stickerEligibleCollections.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>
                    No sticker-ready collections. Add boxes to a collection first, or adjust your search filters.
                  </p>
                ) : (
                  <div className="jet-table-wrap">
                    <table className="jet-table">
                      <thead>
                        <tr>
                          <th>Collection No.</th>
                          <th>Clearing Type</th>
                          <th>Packages</th>
                          <th>Shipper</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stickerEligibleCollections.map((collection) => {
                          const sum = getCollectionSummary(collection?.boxes || []);
                          return (
                            <tr key={collection._id || collection.collectionNo}>
                              <td style={{ fontWeight: 600 }}>{collection.collectionNo || '—'}</td>
                              <td>{clearingTypeLabel(collection.clearingType)}</td>
                              <td>{sum.totalQty}</td>
                              <td>{renderShipperCollectionCell(collection)}</td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    className="jet-btn jet-btn-primary"
                                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
                                    onClick={() => handleCreateStickers(collection)}
                                  >
                                    <Tags className="w-4 h-4" />
                                    Print stickers
                                  </button>
                                  <button
                                    type="button"
                                    className="jet-btn jet-btn-secondary jet-btn-icon"
                                    title="View collection"
                                    onClick={() => handleViewCollection(collection)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'consolidateHbl' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card">
                <div className="jet-table-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <h2 className="jet-table-title" style={{ margin: 0 }}>
                      Consolidated bills
                      {consolidatedCollectionsCount > 0 && (
                        <span style={{ fontWeight: 500, color: 'var(--jet-muted)', fontSize: '0.85rem', marginLeft: 8 }}>
                          ({filteredConsolidatedCollections.length} of {consolidatedCollectionsCount})
                        </span>
                      )}
                    </h2>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button type="button" className="jet-btn jet-btn-secondary" onClick={fetchCollections}>
                        <Search className="w-4 h-4" />
                        Refresh
                      </button>
                      <button
                        type="button"
                        className="jet-btn jet-btn-primary"
                        onClick={() => openConsolidateWizard('create')}
                      >
                        <Plus className="w-4 h-4" />
                        Create consolidated bill
                      </button>
                    </div>
                  </div>
                  {collectionToolbarFilters}
                </div>
                <p style={{ margin: '0 0 1rem', color: 'var(--jet-muted)', fontSize: '0.875rem' }}>
                  Select door-to-door bills and combine them into one UPB or Gift bill for customs declaration.
                </p>
                {loadingCollections ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>Loading…</p>
                ) : consolidatedCollectionsCount === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--jet-muted)' }}>
                    <Layers className="w-10 h-10" style={{ margin: '0 auto 0.75rem', opacity: 0.35 }} />
                    <p style={{ margin: '0 0 1rem' }}>No consolidated bills yet.</p>
                    <button type="button" className="jet-btn jet-btn-primary" onClick={() => openConsolidateWizard('create')}>
                      <Plus className="w-4 h-4" />
                      Door to Door → UPB/Gift
                    </button>
                  </div>
                ) : filteredConsolidatedCollections.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>No consolidated bills match the current filters.</p>
                ) : (
                  <div className="jet-table-wrap">
                    <table className="jet-table">
                      <thead>
                        <tr>
                          <th>Bill No.</th>
                          <th>Date</th>
                          <th>Shipper</th>
                          <th>Clearing</th>
                          <th>D2D bills</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredConsolidatedCollections.map((collection) => (
                          <tr key={collection._id || collection.collectionNo}>
                            <td style={{ fontWeight: 600 }}>{collection.collectionNo || '—'}</td>
                            <td>{formatAdminDate(collection.collectionDate || collection.createdAt)}</td>
                            <td>{renderShipperCollectionCell(collection)}</td>
                            <td>{clearingTypeLabel(collection.clearingType)}</td>
                            <td>{(collection.childCollectionIds || []).length}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="jet-btn jet-btn-primary"
                                  onClick={() => handleCreateHBL(collection)}
                                >
                                  HBL
                                </button>
                                {(() => {
                                  const sc = canPrintStickers(collection, collections);
                                  return (
                                    <button
                                      type="button"
                                      className="jet-btn jet-btn-secondary"
                                      onClick={() => handleCreateStickers(collection)}
                                      disabled={!sc.ok}
                                      title={sc.reason || 'Print A6 stickers'}
                                    >
                                      Stickers
                                    </button>
                                  );
                                })()}
                                <button
                                  type="button"
                                  className="jet-btn jet-btn-secondary"
                                  onClick={() => openConsolidateWizard('edit', collection)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="jet-btn jet-btn-secondary jet-btn-icon"
                                  title="View"
                                  onClick={() => handleViewCollection(collection)}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {['docInvoices', 'docReceipts', 'docHbl'].map((docTab) => {
            const docCollections = docTab === 'docHbl' ? filteredHblCollections : filteredRegularCollections;
            const docTotalCount = docTab === 'docHbl' ? hblCollectionsCount : regularCollectionsCount;
            return (
            <React.Fragment key={docTab}>
              {activeTab === docTab && (
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                  <div className="jet-card">
                    <div className="jet-table-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <h2 className="jet-table-title" style={{ margin: 0 }}>
                          {(pageMeta[docTab] || pageMeta.docInvoices).title}
                          {collections.length > 0 && (
                            <span style={{ fontWeight: 500, color: 'var(--jet-muted)', fontSize: '0.85rem', marginLeft: 8 }}>
                              ({docCollections.length} of {docTotalCount})
                            </span>
                          )}
                        </h2>
                        <button type="button" className="jet-btn jet-btn-secondary" onClick={fetchCollections}>
                          <Search className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>
                      {collectionToolbarFilters}
                    </div>
                    {loadingCollections ? (
                      <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>Loading…</p>
                    ) : collections.length === 0 ? (
                      <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>No collections available.</p>
                    ) : docCollections.length === 0 ? (
                      <p style={{ textAlign: 'center', color: 'var(--jet-muted)', padding: '2rem' }}>No collections match the current filters.</p>
                    ) : (
                      <div className="jet-table-wrap">
                        <table className="jet-table">
                          <thead>
                            <tr>
                              <th>Collection No.</th>
                              <th>Date</th>
                              <th>Shipper</th>
                              <th>Clearing</th>
                              <th>Total (RM)</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {docCollections.map((collection) => (
                              <tr key={collection._id || collection.collectionNo}>
                                <td style={{ fontWeight: 600 }}>
                                  {collection.collectionNo || '—'}
                                  {docTab === 'docHbl' && isConsolidatedParent(collection) && (
                                    <span style={{ display: 'block', fontSize: '0.7rem', color: '#1d4ed8', fontWeight: 600 }}>
                                      Consolidated ({collection.childCollectionIds?.length || 0} D2D)
                                    </span>
                                  )}
                                </td>
                                <td>{formatAdminDate(collection.collectionDate || collection.createdAt)}</td>
                                <td>{renderShipperCollectionCell(collection)}</td>
                                <td>{clearingTypeLabel(collection.clearingType)}</td>
                                <td>{formatCurrencyRM(collectionRowTotalPrice(collection))}</td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                    {docTab === 'docInvoices' && (
                                      <button type="button" className="jet-btn jet-btn-primary" onClick={() => handleCreateInvoice(collection)}>Invoice</button>
                                    )}
                                    {docTab === 'docReceipts' && (
                                      <button type="button" className="jet-btn jet-btn-primary" onClick={() => handleCreateReceipt(collection)}>Receipt</button>
                                    )}
                                    {docTab === 'docHbl' && (
                                      <>
                                        <button
                                          type="button"
                                          className="jet-btn jet-btn-primary"
                                          onClick={() => handleCreateHBL(collection)}
                                          disabled={Boolean(collection.consolidatedInto)}
                                          title={collection.consolidatedInto ? 'This door-to-door bill is linked to a consolidated bill' : 'Generate HBL'}
                                        >
                                          HBL
                                        </button>
                                        {isConsolidatedParent(collection) && (
                                          <button
                                            type="button"
                                            className="jet-btn jet-btn-secondary"
                                            onClick={() => openConsolidateWizard('edit', collection)}
                                          >
                                            Edit
                                          </button>
                                        )}
                                      </>
                                    )}
                                    <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" title="View" onClick={() => handleViewCollection(collection)}>
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </React.Fragment>
            );
          })}

          {/* User management (portal accounts) */}
          {activeTab === 'employees' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card" style={{ marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.5rem' }}>Add user</h2>
                <p style={{ color: 'var(--jet-muted)', fontSize: '0.875rem', margin: '0 0 1rem' }}>
                  New accounts can sign in at <strong>/login</strong>. Staff with operations or finance access also open this dashboard after login. Administrators use <strong>/admin/login</strong> with MFA.
                </p>
                <form onSubmit={handleCreatePortalUser} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Name</label>
                    <input className="form-input" value={newPortalUser.name} onChange={(e) => setNewPortalUser((p) => ({ ...p, name: e.target.value }))} placeholder="Full name" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Email</label>
                    <input type="email" className="form-input" value={newPortalUser.email} onChange={(e) => setNewPortalUser((p) => ({ ...p, email: e.target.value }))} placeholder="email@example.com" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Password</label>
                    <input type="password" className="form-input" value={newPortalUser.password} onChange={(e) => setNewPortalUser((p) => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" autoComplete="new-password" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Role</label>
                    <select className="form-input" value={newPortalUser.role} onChange={(e) => setNewPortalUser((p) => ({ ...p, role: e.target.value }))}>
                      <option value="customer">Customer</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  {newPortalUser.role === 'staff' ? (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Staff access</label>
                      <select
                        className="form-input"
                        value={newPortalUser.staffDepartment}
                        onChange={(e) => setNewPortalUser((p) => ({ ...p, staffDepartment: e.target.value }))}
                      >
                        <option value="full">Full operations</option>
                        <option value="ops">Operations only</option>
                        <option value="finance">Finance only</option>
                      </select>
                    </div>
                  ) : null}
                  <div>
                    <button type="submit" className="jet-btn jet-btn-primary" disabled={creatingPortalUser} style={{ width: '100%' }}>
                      {creatingPortalUser ? 'Creating…' : 'Create user'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="jet-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                    All users
                    {appUsers.length > 0 && (
                      <span style={{ fontWeight: 500, color: 'var(--jet-muted)', fontSize: '0.85rem', marginLeft: 8 }}>
                        ({filteredAppUsers.length} of {appUsers.length})
                      </span>
                    )}
                  </h2>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={fetchPortalUsers}>Refresh</button>
                </div>
                <div className="jet-admin-filter-row">
                  <div className="jet-admin-filter-search">
                    <Search
                      className="w-4 h-4"
                      style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0.5,
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="search"
                      className="form-input"
                      value={usersFilter.query}
                      onChange={(e) => setUsersFilter((f) => ({ ...f, query: e.target.value }))}
                      placeholder="Search name or email…"
                      style={{ paddingLeft: 34 }}
                      aria-label="Search users"
                    />
                  </div>
                  <select
                    className="form-input"
                    style={{ width: 'auto', minWidth: 130 }}
                    value={usersFilter.role}
                    onChange={(e) => setUsersFilter((f) => ({ ...f, role: e.target.value }))}
                    aria-label="Filter by role"
                  >
                    <option value="all">All roles</option>
                    <option value="customer">Customer</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Administrator</option>
                  </select>
                  <select
                    className="form-input"
                    style={{ width: 'auto', minWidth: 130 }}
                    value={usersFilter.active}
                    onChange={(e) => setUsersFilter((f) => ({ ...f, active: e.target.value }))}
                    aria-label="Filter by status"
                  >
                    <option value="all">Active &amp; inactive</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive only</option>
                  </select>
                  <button
                    type="button"
                    className="jet-btn jet-btn-secondary"
                    onClick={() => setUsersFilter({ query: '', role: 'all', active: 'all' })}
                  >
                    Clear filters
                  </button>
                </div>
                {appUsersError && (
                  <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{appUsersError}</p>
                )}
                {appUsersLoading ? (
                  <p style={{ color: 'var(--jet-muted)' }}>Loading…</p>
                ) : (
                  <div className="jet-table-wrap">
                    <table className="jet-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Access</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAppUsers.map((u) => (
                          <tr key={u._id}>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td>
                              <select
                                className="form-input"
                                style={{ minWidth: '120px', padding: '0.35rem 0.5rem' }}
                                value={u.role}
                                onChange={(e) => updatePortalUserRole(u._id, e.target.value)}
                              >
                                <option value="customer">Customer</option>
                                <option value="staff">Staff</option>
                                <option value="admin">Administrator</option>
                              </select>
                            </td>
                            <td>
                              {u.role === 'staff' ? (
                                <select
                                  className="form-input"
                                  style={{ minWidth: '130px', padding: '0.35rem 0.5rem' }}
                                  value={u.staffDepartment || 'full'}
                                  onChange={(e) => updatePortalUserDepartment(u._id, e.target.value)}
                                >
                                  <option value="full">Full</option>
                                  <option value="ops">Operations</option>
                                  <option value="finance">Finance</option>
                                </select>
                              ) : (
                                <span style={{ color: 'var(--jet-muted)', fontSize: '0.85rem' }}>—</span>
                              )}
                            </td>
                            <td>{u.isActive ? <span className="jet-badge jet-badge-success">Active</span> : <span className="jet-badge jet-badge-warn">Inactive</span>}</td>
                            <td>
                              <button
                                type="button"
                                className="jet-btn jet-btn-secondary"
                                style={{ padding: '0.35rem 0.65rem', fontSize: '0.8rem' }}
                                onClick={() => updatePortalUserActive(u._id, !u.isActive)}
                              >
                                {u.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {appUsers.length === 0 && !appUsersLoading && (
                      <p style={{ color: 'var(--jet-muted)', padding: '1rem', margin: 0 }}>No users found.</p>
                    )}
                    {appUsers.length > 0 && filteredAppUsers.length === 0 && (
                      <p style={{ color: 'var(--jet-muted)', padding: '1rem', margin: 0 }}>No users match the current filters.</p>
                    )}
                  </div>
                )}
                <p style={{ fontSize: '0.8rem', color: 'var(--jet-muted)', marginTop: '1rem', marginBottom: 0 }}>
                  You cannot remove the last active administrator. New admins should use <strong>/admin/login</strong> and complete authenticator setup when prompted.
                </p>
              </div>
            </motion.div>
          )}

          {/* Finance Tab */}
          {activeTab === 'finance' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <AdminFinancePanel
                apiBase={API_BASE}
                authToken={authUser?.token}
                isActive={activeTab === 'finance'}
              />
            </motion.div>
          )}

          {activeTab === 'reviews' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <AdminReviewsPanel
                apiBase={API_BASE}
                authToken={authUser?.token}
                isActive={activeTab === 'reviews'}
              />
            </motion.div>
          )}

          {activeTab === 'enquiries' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <AdminEnquiriesPanel
                apiBase={API_BASE}
                authToken={authUser?.token}
                isActive={activeTab === 'enquiries'}
              />
            </motion.div>
          )}

          {activeTab === 'reports' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <AdminReportsPanel
                apiBase={API_BASE}
                authToken={authUser?.token}
                isActive={activeTab === 'reports'}
              />
            </motion.div>
          )}

          {activeTab === 'packageScan' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <AdminPackageScan />
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="jet-card" style={{ marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.5rem' }}>Appearance</h2>
                <p style={{ color: 'var(--jet-muted)', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Choose light or dark mode for the admin panel and public website. Your choice is saved on this device.
                </p>
                <ThemeToggle variant="settings" />
              </div>
              <div className="jet-card" style={{ marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Settings</h2>
                <p style={{ color: 'var(--jet-muted)', marginTop: 0, marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                  Additional tools not shown in the main menu.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={() => setActiveTab('shipments')}>Open Shipments</button>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={() => setActiveTab('finance')}>Open Finance</button>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={() => setActiveTab('boxCollection')}>Add Collection</button>
                </div>
              </div>
              <div className="jet-card">
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.5rem' }}>Sign-in security (your account)</h2>
                <p style={{ color: 'var(--jet-muted)', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Two-step verification (authenticator app + email code) is configured when you first sign in at{' '}
                  <strong>/admin/login</strong>. Manage it here for <strong>your</strong> admin login only.
                </p>
                {securityLoading && !securityMessage.text ? (
                  <p style={{ color: 'var(--jet-muted)', margin: 0 }}>Loading security status…</p>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>Two-step verification:</span>
                      {securityMfaEnabled ? (
                        <span className="jet-badge jet-badge-success">Active</span>
                      ) : (
                        <span className="jet-badge jet-badge-warn">Not active</span>
                      )}
                      <span style={{ fontSize: '0.85rem', color: 'var(--jet-muted)' }}>
                        {securityMfaEnabled
                          ? 'Sign-in requires your password plus an authenticator or email code.'
                          : 'Your next admin sign-in will prompt you to scan a QR code (unless you sign in before enabling it).'}
                      </span>
                    </div>
                    {securityMfaEnabled && (
                      <div
                        className="jet-settings-muted-box"
                        style={{
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '1px solid var(--jet-border)',
                          background: 'var(--jet-bg)',
                          maxWidth: '420px',
                        }}
                      >
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                          Turn off two-step verification (enter your password)
                        </label>
                        <input
                          type="password"
                          className="form-input"
                          value={mfaDisablePassword}
                          onChange={(e) => setMfaDisablePassword(e.target.value)}
                          placeholder="Your login password"
                          autoComplete="current-password"
                          style={{ marginBottom: '0.75rem' }}
                        />
                        <button
                          type="button"
                          className="jet-btn jet-btn-secondary"
                          disabled={securityLoading}
                          onClick={handleDisableMfa}
                        >
                          {securityLoading ? 'Working…' : 'Disable two-step verification'}
                        </button>
                      </div>
                    )}
                    {securityMessage.text && (
                      <p
                        style={{
                          marginTop: '1rem',
                          marginBottom: 0,
                          fontSize: '0.9rem',
                          color: securityMessage.type === 'ok' ? '#166534' : '#dc2626',
                        }}
                      >
                        {securityMessage.text}
                      </p>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}

          {/* Customers Tab */}
          {activeTab === 'customers' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="jet-card jet-card--flush">
                <AdminCustomersPanel
                  customerTab={customerTab}
                  onTabChange={setCustomerTab}
                  searchQuery={customersFilter.query}
                  onSearchChange={(v) => setCustomersFilter((f) => ({ ...f, query: v }))}
                  onClearSearch={() => setCustomersFilter({ query: '' })}
                  shippersList={shippersList}
                  filteredShippersList={filteredShippersList}
                  loadingShippers={loadingShippers}
                  onRefreshShippers={fetchShippersList}
                  selectedShipperCustomer={selectedShipperCustomer}
                  shipperForm={shipperForm}
                  setShipperForm={setShipperForm}
                  onViewShipper={viewShipperDetails}
                  onEditShipper={openShipperEditor}
                  onResetShipper={resetShipperForm}
                  onSaveShipper={saveShipperCustomer}
                  savingShipper={savingShipper}
                  consigneesList={consigneesList}
                  filteredConsigneesList={filteredConsigneesList}
                  loadingConsignees={loadingConsignees}
                  onRefreshConsignees={fetchConsigneesList}
                  selectedConsigneeCustomer={selectedConsigneeCustomer}
                  consigneeForm={consigneeForm}
                  setConsigneeForm={setConsigneeForm}
                  onViewConsignee={viewConsigneeDetails}
                  onEditConsignee={openConsigneeEditor}
                  onResetConsignee={resetConsigneeForm}
                  onSaveConsignee={saveConsigneeCustomer}
                  savingConsignee={savingConsignee}
                  customerPanelMode={customerPanelMode}
                  normalizeMobiles={normalizeMobiles}
                  getPreviewUrl={getPreviewUrl}
                  consigneeIdLabel={consigneeIdLabel}
                  idTypeLabel={idTypeLabel}
                  consigneeMatchesId={consigneeMatchesId}
                  onImageChoose={handleCustomerImageChoose}
                />
              </div>
            </motion.div>
          )}

          {/* Add Shipment Modal */}
          {showBoxPreview && (
            <div className="jet-admin-modal-overlay jet-admin-modal-overlay--dark">
              <motion.div className="card" style={{ width: '100%', maxWidth: '900px', maxHeight: 'min(90vh, 100dvh - 1rem)', overflow: 'auto' }} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Box Collection Summary</h2>
                  <button className="btn btn-secondary" onClick={()=>setShowBoxPreview(false)}>Close</button>
                </div>

                {/* Meta */}
                <div className="grid grid-3" style={{ gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Collection No.</p>
                    <p style={{ fontWeight: 600 }}>{boxCollectionForm.collectionNo || '—'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Collection Date</p>
                    <p style={{ fontWeight: 600 }}>{boxCollectionForm.collectionDate || '—'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Clearing Type</p>
                    <p style={{ fontWeight: 600 }}>{boxCollectionForm.clearingType || '—'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Box dimensions (form default)</p>
                    <p style={{ fontWeight: 600 }}>{(boxCollectionForm.boxDimensionUnit || 'cm').toUpperCase()}</p>
                  </div>
                </div>

                {/* Shipper/Consignee */}
                <div className="grid grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                  <div className="admin-card" style={{ padding: '12px' }}>
                    <div className="card-title">Shipper</div>
                    <div style={{ fontSize: '0.95rem' }}>
                      <div><strong>Name:</strong> {boxCollectionForm.shipper.nameWithInitials || '—'}</div>
                      <div><strong>Passport No:</strong> {boxCollectionForm.shipper.passportNo || '—'}</div>
                      <div><strong>Address:</strong> {boxCollectionForm.shipper.address || '—'}</div>
                      <div><strong>Mobiles:</strong> {boxCollectionForm.shipper.mobiles.filter(Boolean).join(', ') || '—'}</div>
                      {boxCollectionForm.shipper.passportPhoto ? (
                        <div style={{ marginTop: '0.5rem' }}>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Passport photo (preview)</div>
                          <img src={getPreviewUrl(boxCollectionForm.shipper.passportPhoto)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="admin-card" style={{ padding: '12px' }}>
                    <div className="card-title">Consignee</div>
                    <div style={{ fontSize: '0.95rem' }}>
                      <div><strong>Name:</strong> {boxCollectionForm.consignee.nameWithInitials || '—'}</div>
                      <div>
                        <strong>ID type:</strong>{' '}
                        {boxCollectionForm.consignee.idType === 'passport'
                          ? 'Passport'
                          : boxCollectionForm.consignee.idType === 'nic'
                            ? 'NIC'
                            : boxCollectionForm.consignee.idType === 'driving_licence'
                              ? 'Driving licence'
                              : '—'}
                      </div>
                      {boxCollectionForm.consignee.idType === 'passport' && (
                        <div><strong>Passport No:</strong> {boxCollectionForm.consignee.passportNo || '—'}</div>
                      )}
                      {boxCollectionForm.consignee.idType === 'nic' && (
                        <div><strong>NIC No:</strong> {boxCollectionForm.consignee.nicNo || '—'}</div>
                      )}
                      {boxCollectionForm.consignee.idType === 'driving_licence' && (
                        <div><strong>Driving licence No:</strong> {boxCollectionForm.consignee.drivingLicenceNo || '—'}</div>
                      )}
                      <div><strong>Address:</strong> {boxCollectionForm.consignee.address || '—'}</div>
                      <div><strong>Mobiles:</strong> {boxCollectionForm.consignee.mobiles.filter(Boolean).join(', ') || '—'}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                        {boxCollectionForm.consignee.idType === 'passport' && boxCollectionForm.consignee.passportPhoto ? (
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Passport</div>
                            <img src={getPreviewUrl(boxCollectionForm.consignee.passportPhoto)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                          </div>
                        ) : null}
                        {boxCollectionForm.consignee.idType === 'nic' && boxCollectionForm.consignee.nicFront ? (
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>NIC front</div>
                            <img src={getPreviewUrl(boxCollectionForm.consignee.nicFront)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                          </div>
                        ) : null}
                        {boxCollectionForm.consignee.idType === 'nic' && boxCollectionForm.consignee.nicBack ? (
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>NIC back</div>
                            <img src={getPreviewUrl(boxCollectionForm.consignee.nicBack)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                          </div>
                        ) : null}
                        {boxCollectionForm.consignee.idType === 'driving_licence' && boxCollectionForm.consignee.drivingLicencePhoto ? (
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Driving licence</div>
                            <img src={getPreviewUrl(boxCollectionForm.consignee.drivingLicencePhoto)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Boxes Summary Table */}
                <div className="table-scroll">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Box Type</th>
                        <th>Official Box Name</th>
                        <th>Description</th>
                        <th>Unit</th>
                        <th>L</th>
                        <th>W</th>
                        <th>H</th>
                        <th>Unit CBM</th>
                        <th>Total CBM</th>
                        <th>Unit Wt</th>
                        <th>Tot Wt</th>
                        <th>Price (RM)</th>
                        <th>Qty</th>
                        <th>Line (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boxCollectionForm.boxes.map((b, i) => {
                        const qty = parseFloat(b.quantity) || 0;
                        const price = parseFloat(b.price) || 0;
                        const lineTotal = qty * price;
                        return (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{b.boxType || '—'}</td>
                            <td>{b.officialBoxName || '—'}</td>
                            <td>{b.description || '—'}</td>
                            <td>{(b.dimensionsUnit || boxCollectionForm.boxDimensionUnit || 'cm').toUpperCase()}</td>
                            <td>{b.length ?? '—'}</td>
                            <td>{b.width ?? '—'}</td>
                            <td>{b.height ?? '—'}</td>
                            <td>{b.cbm ?? '—'}</td>
                            <td>{b.totalCbm || '—'}</td>
                            <td>{b.weight ?? '—'}</td>
                            <td>{b.totalWeight || '—'}</td>
                            <td>{formatOptionalRM(b.price)}</td>
                            <td>{b.quantity}</td>
                            <td>{formatCurrencyRM(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Charges + grand total (matches form summary) */}
                {(() => {
                  const p = getCollectionPricingSummary();
                  return (
                    <div className="admin-card" style={{ padding: '12px', marginTop: '1rem' }}>
                      <div className="card-title" style={{ marginBottom: '0.5rem' }}>Charges &amp; total</div>
                      <div className="jet-admin-kv-grid jet-admin-kv-grid--lg">
                        <div>Total packages</div><div>{p.totalQty}</div>
                        <div>Total CBM</div><div>{p.totalCbm.toFixed(2)}</div>
                        <div>Total weight</div><div>{p.totalWeight.toFixed(2)} kg</div>
                        <div>Sub total (boxes)</div><div>{formatCurrencyRM(p.subTotal)}</div>
                        <div>Transport</div><div>{formatCurrencyRM(p.transportCharge)}</div>
                        <div>Packing</div><div>{formatCurrencyRM(p.packingCharge)}</div>
                        <div>Other</div><div>{formatCurrencyRM(p.otherCharge)}</div>
                        <div>Discount ({p.discountType === 'percentage' ? `${p.discountValue}%` : 'RM'})</div><div>− {formatCurrencyRM(p.discountAmount)}</div>
                        <div style={{ fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: '0.45rem' }}>Grand total</div>
                        <div style={{ fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: '0.45rem' }}>{formatCurrencyRM(p.totalPrice)}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Totals + actions */}
                <div className="admin-grid-2" style={{ marginTop: '1rem' }}>
                  <div className="admin-card" style={{ padding: '12px' }}>
                    {(() => {
                      const t = getBoxTotals();
                      return (
                        <div className="jet-admin-form-grid-2 jet-admin-form-grid-2--tight">
                          <div><strong>Boxes — total qty</strong></div><div>{t.totalQty}</div>
                          <div><strong>Boxes — total CBM</strong></div><div>{t.totalCbm}</div>
                          <div><strong>Boxes — total weight (kg)</strong></div><div>{Number(t.totalWeight).toFixed(2)}</div>
                          <div><strong>Boxes — subtotal (RM)</strong></div><div>{formatCurrencyRM(t.totalPrice)}</div>
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowBoxPreview(false)}>Back</button>
                    <button
                      type="button"
                      className="btn"
                      onClick={handleSubmitCollection}
                      disabled={isSubmittingCollection}
                    >
                      {isSubmittingCollection ? 'Submitting…' : 'Submit Collection'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </main>
      </div>

      {/* Receipt Modal */}
      {showReceipt && (
        <div className="jet-admin-doc-shell">
          <Receipt
            key={String(selectedCollection?._id || selectedCollection?.id || 'receipt')}
            collection={selectedCollection}
            shipper={selectedShipper}
            consignee={selectedConsignee}
            onClose={handleCloseReceipt}
            onPrint={handlePrintReceipt}
            onSaveReceipt={handleSaveReceiptPayments}
          />
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoice && (
        <div className="jet-admin-doc-shell">
          <Invoice 
            collection={selectedCollection}
            shipper={selectedShipper}
            consignee={selectedConsignee}
            onClose={handleCloseInvoice}
            onPrint={handlePrintInvoice}
          />
        </div>
      )}

      {/* HBL Modal */}
      {showHBL && (
        <div className="jet-admin-doc-shell">
          <HBL 
            collection={selectedCollection}
            shipper={selectedShipper}
            consignee={selectedConsignee}
            childCollections={hblChildCollections}
            onClose={handleCloseHBL}
            onPrint={handlePrintHBL}
          />
        </div>
      )}

      {showStickers && selectedCollection && (
        <div className="jet-admin-doc-shell">
          <PackageStickers
            collection={selectedCollection}
            parentCollection={stickerParentCollection}
            consignee={selectedConsignee}
            childCollections={stickerChildCollections}
            onClose={handleCloseStickers}
            onEnsurePackages={ensurePackagesForSticker}
          />
        </div>
      )}

      {showConsolidateWizard && (
        <AdminConsolidateWizard
          collections={collections}
          mode={consolidateWizardMode}
          parentCollection={consolidateParent}
          onClose={closeConsolidateWizard}
          onSuccess={() => fetchCollections()}
          loadCollectionPeople={loadCollectionPeople}
        />
      )}

      {/* Collection Preview Modal */}
      {showCollectionPreview && (
        <div className="jet-admin-modal-overlay jet-admin-modal-overlay--dark">
          <div className="jet-admin-modal-panel">
            <div className="jet-admin-modal-panel__body">
              {/* Header */}
              <div className="jet-admin-modal-header">
                <h2>Collection Preview</h2>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {!isEditingCollection && (
                    <button
                      type="button"
                      className="jet-btn jet-btn-primary"
                      onClick={handleEditCollection}
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    className="jet-btn jet-btn-danger"
                    onClick={handleCloseCollectionPreview}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Collection Details */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 className="jet-modal-section-title">Collection Information</h3>
                <div className="jet-surface-muted" style={{ marginBottom: '1rem' }}>
                  {isEditingCollection ? (
                    <div className="jet-admin-form-grid-2">
                      <div>
                        <label className="form-label">Collection No:</label>
                        <input 
                          type="text" 
                          className="form-input"
                          value={editingCollectionData?.collectionNo || ''}
                          onChange={(e) => setEditingCollectionData({...editingCollectionData, collectionNo: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="form-label">Clearing Type:</label>
                        <select 
                          className="form-input"
                          value={editingCollectionData?.clearingType || ''}
                          onChange={(e) => setEditingCollectionData({...editingCollectionData, clearingType: e.target.value})}
                        >
                          <option value="gift">Gift</option>
                          <option value="door_to_door">Door to Door</option>
                          <option value="upb">UPB</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Collection Date:</label>
                        <input
                          type="date"
                          className="form-input"
                          value={editingCollectionData?.collectionDate || ''}
                          onChange={(e) => setEditingCollectionData({...editingCollectionData, collectionDate: e.target.value})}
                        />
                      </div>
                      <div>
                        <strong>Created:</strong> {selectedCollection?.createdAt ? new Date(selectedCollection.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                      <div>
                        <strong>Total Boxes:</strong> {selectedCollection?.boxes?.length || 0}
                      </div>
                      {selectedCollection?.lastUpdated && (
                        <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontWeight: 'bold' }}>
                          <strong>Last Updated:</strong> {new Date(selectedCollection.lastUpdated).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="jet-admin-form-grid-2">
                      <div>
                        <strong>Collection No:</strong> {selectedCollection?.collectionNo}
                      </div>
                      <div>
                        <strong>Clearing Type:</strong> {selectedCollection?.clearingType?.toUpperCase()}
                      </div>
                      <div>
                        <strong>Collection Date:</strong> {(selectedCollection?.collectionDate || selectedCollection?.createdAt) ? new Date(selectedCollection.collectionDate || selectedCollection.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                      <div>
                        <strong>Created:</strong> {selectedCollection?.createdAt ? new Date(selectedCollection.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                      <div>
                        <strong>Total Boxes:</strong> {selectedCollection?.boxes?.length || 0}
                      </div>
                      {selectedCollection?.lastUpdated && (
                        <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontWeight: 'bold' }}>
                          <strong>Last Updated:</strong> {new Date(selectedCollection.lastUpdated).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {isEditingCollection && (
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="jet-btn jet-btn-success"
                      onClick={handleSaveCollectionUpdates}
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Shipper Details */}
              {selectedShipper && (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <h3 className="jet-modal-section-title--row">Shipper Details</h3>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => viewShipperDetails(selectedShipper.passportNo || selectedCollection?.shipperPassportNo)}
                    >
                      View in Shippers
                    </button>
                  </div>
                  <div className="jet-surface-muted">
                    {isEditingCollection ? (
                      <div className="jet-admin-form-grid-2">
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Name:</label>
                          <input 
                            type="text" 
                            value={selectedShipper.nameWithInitials || ''}
                            onChange={(e) => {
                              // Update shipper data locally for display
                              setSelectedShipper({...selectedShipper, nameWithInitials: e.target.value});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Passport No:</label>
                          <input 
                            type="text" 
                            value={editingCollectionData?.shipperPassportNo || ''}
                            onChange={(e) => setEditingCollectionData({...editingCollectionData, shipperPassportNo: e.target.value})}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Address:</label>
                          <textarea 
                            value={selectedShipper.address || ''}
                            onChange={(e) => {
                              setSelectedShipper({...selectedShipper, address: e.target.value});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              minHeight: '60px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mobile:</label>
                          <input 
                            type="text" 
                            value={selectedShipper.mobiles?.join(', ') || ''}
                            onChange={(e) => {
                              setSelectedShipper({...selectedShipper, mobiles: e.target.value.split(',').map(m => m.trim())});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                            placeholder="Enter mobile numbers separated by commas"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="jet-admin-form-grid-2">
                        <div>
                          <strong>Name:</strong> {selectedShipper.nameWithInitials}
                        </div>
                        <div>
                          <strong>Passport No:</strong> {selectedShipper.passportNo}
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong>Address:</strong> {selectedShipper.address}
                        </div>
                        <div>
                          <strong>Mobile:</strong> {selectedShipper.mobiles?.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Consignee Details */}
              {selectedConsignee && (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <h3 className="jet-modal-section-title--row">Consignee Details</h3>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      style={{ fontSize: '0.85rem' }}
                      onClick={() => viewConsigneeDetails(selectedCollection?.consigneeId || consigneeIdLabel(selectedConsignee))}
                    >
                      View in Consignees
                    </button>
                  </div>
                  <div className="jet-surface-muted">
                    {isEditingCollection ? (
                      <div className="jet-admin-form-grid-2">
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Name:</label>
                          <input 
                            type="text" 
                            value={selectedConsignee.nameWithInitials || ''}
                            onChange={(e) => {
                              setSelectedConsignee({...selectedConsignee, nameWithInitials: e.target.value});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>ID (Passport/NIC):</label>
                          <input 
                            type="text" 
                            value={editingCollectionData?.consigneeId || ''}
                            onChange={(e) => setEditingCollectionData({...editingCollectionData, consigneeId: e.target.value})}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Address:</label>
                          <textarea 
                            value={selectedConsignee.address || ''}
                            onChange={(e) => {
                              setSelectedConsignee({...selectedConsignee, address: e.target.value});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px',
                              minHeight: '60px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mobile:</label>
                          <input 
                            type="text" 
                            value={selectedConsignee.mobiles?.join(', ') || ''}
                            onChange={(e) => {
                              setSelectedConsignee({...selectedConsignee, mobiles: e.target.value.split(',').map(m => m.trim())});
                            }}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #ddd', 
                              borderRadius: '4px' 
                            }}
                            placeholder="Enter mobile numbers separated by commas"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="jet-admin-form-grid-2">
                        <div>
                          <strong>Name:</strong> {selectedConsignee.nameWithInitials}
                        </div>
                        <div>
                          <strong>ID:</strong> {selectedConsignee.passportNo || selectedConsignee.nicNo}
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <strong>Address:</strong> {selectedConsignee.address}
                        </div>
                        <div>
                          <strong>Mobile:</strong> {selectedConsignee.mobiles?.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Box Details */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 className="jet-modal-section-title--row">Box Details</h3>
                  {isEditingCollection && (
                    <button
                      onClick={handleAddBox}
                      style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ➕ Add Box
                    </button>
                  )}
                </div>

                {currentCollectionBoxes.length > 0 ? (
                  <div className="jet-table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--jet-royal, #0056f1)', color: 'white' }}>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Box Type</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Official Box Name</th>
                          <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Description</th>
                          <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Dimensions</th>
                          <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>CBM</th>
                          <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Weight</th>
                          <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Price</th>
                          <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Qty</th>
                          {isEditingCollection && (
                            <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {currentCollectionBoxes.map((box, index) => (
                          <tr key={box?._id || box?.id || `collection-box-${index}`}>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input
                                  type="text"
                                  value={box.boxType || ''}
                                  onChange={(e) => handleUpdateBox(index, 'boxType', e.target.value)}
                                  style={{ width: '100%', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              ) : (
                                box.boxType || 'N/A'
                              )}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input
                                  type="text"
                                  value={box.officialBoxName || ''}
                                  onChange={(e) => handleUpdateBox(index, 'officialBoxName', e.target.value)}
                                  style={{ width: '100%', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              ) : (
                                box.officialBoxName || 'N/A'
                              )}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input
                                  type="text"
                                  value={box.description || ''}
                                  onChange={(e) => handleUpdateBox(index, 'description', e.target.value)}
                                  style={{ width: '100%', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                              ) : (
                                box.description || 'N/A'
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                  <input type="number" value={box.length || ''} onChange={(e) => handleUpdateBox(index, 'length', e.target.value)} placeholder="L" style={{ width: 'min(40px, 22vw)', minWidth: '32px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                  <span>x</span>
                                  <input type="number" value={box.width || ''} onChange={(e) => handleUpdateBox(index, 'width', e.target.value)} placeholder="W" style={{ width: 'min(40px, 22vw)', minWidth: '32px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                  <span>x</span>
                                  <input type="number" value={box.height || ''} onChange={(e) => handleUpdateBox(index, 'height', e.target.value)} placeholder="H" style={{ width: 'min(40px, 22vw)', minWidth: '32px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                  <select value={box.dimensionsUnit || 'cm'} onChange={(e) => handleUpdateBox(index, 'dimensionsUnit', e.target.value)} style={{ padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    <option value="cm">CM</option>
                                    <option value="inch">Inch</option>
                                  </select>
                                </div>
                              ) : (
                                `${box.length} x ${box.width} x ${box.height} ${box.dimensionsUnit}`
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input type="number" step="0.01" value={box.cbm || ''} onChange={(e) => handleUpdateBox(index, 'cbm', e.target.value)} style={{ width: '80px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                              ) : (
                                box.cbm
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input type="number" step="0.1" value={box.weight || ''} onChange={(e) => handleUpdateBox(index, 'weight', e.target.value)} style={{ width: '80px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                              ) : (
                                `${box.weight} kg`
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input type="number" step="0.01" value={box.price || ''} onChange={(e) => handleUpdateBox(index, 'price', e.target.value)} style={{ width: '80px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                              ) : (
                                formatOptionalRM(box.price)
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                              {isEditingCollection ? (
                                <input type="number" min="1" value={box.quantity || 1} onChange={(e) => handleUpdateBox(index, 'quantity', parseInt(e.target.value, 10))} style={{ width: '60px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }} />
                              ) : (
                                box.quantity
                              )}
                            </td>
                            {isEditingCollection && (
                              <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                                <button
                                  onClick={() => handleRemoveBox(index)}
                                  style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                >
                                  🗑️
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="jet-surface-muted jet-modal-empty">
                    No boxes added yet
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="jet-collection-summary">
                <h3>Collection Summary</h3>
                <div className="jet-admin-summary-stats">
                  <div><strong>Total Boxes:</strong> {collectionSummary.totalBoxes}</div>
                  <div><strong>Total CBM:</strong> {collectionSummary.totalCbm.toFixed(2)}</div>
                  <div><strong>Total Weight:</strong> {collectionSummary.totalWeight.toFixed(2)} kg</div>
                  <div><strong>Total Price:</strong> RM {collectionSummary.totalPrice.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
